// src/pages/nafed/pacs/PacsDocumentCenter.js

import React, { useMemo, useState } from "react";
import {
  arrayUnion,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";
import {
  buildPacsDocumentDeletionAudit,
  canDeletePacsDocumentRecord,
  PACS_DOCUMENT_TYPES,
} from "./generatePacsDocuments";
import {
  buildPacsBilingualDocumentHtml,
} from "./pacsOfficialTemplates";
import {
  downloadOfficialPdf,
  officialHtmlToPdfBlob,
  openOfficialPreview,
} from "./officialPdf";
import { createZipBlob, downloadBlob } from "./simpleZip";
import { safeFilePart } from "./pacsWorkflowUtils";

const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;
const MAX_PHOTO_SIZE = 8 * 1024 * 1024;
const MAX_LETTERHEAD_SIZE = 10 * 1024 * 1024;

// Keep the complete evidence implementation available without making it a
// submission gate. Set this to true in future only if PACS photo/GPS evidence
// becomes mandatory again.
const REQUIRE_MEETING_EVIDENCE = false;

const today = () => new Date().toISOString().slice(0, 10);

const clean = (value) => String(value ?? "").trim();

const firstValue = (...values) => {
  for (const value of values) {
    const normalized = clean(value);
    if (normalized) return normalized;
  }
  return "";
};

const numericCapacity = (...values) => {
  for (const value of values) {
    const match = clean(value)
      .replace(/,/g, "")
      .match(/\d+(?:\.\d+)?/);
    if (match && Number(match[0]) > 0) return Number(match[0]);
  }
  return 0;
};

const safeFileName = (value = "document") =>
  String(value)
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 120);

const formatDate = (value) => {
  if (!value) return "Not available";

  const date =
    value?.toDate instanceof Function ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const sha256 = async (blob) => {
  if (!window.crypto?.subtle) return "";

  const buffer = await blob.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const validatePdf = (file) => {
  if (!file) return "Please select a signed PDF.";

  const isPdf =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

  if (!isPdf) return "Only PDF files are allowed.";

  if (file.size > MAX_DOCUMENT_SIZE) {
    return "PDF size cannot exceed 10 MB.";
  }

  return "";
};

const validatePhoto = (file) => {
  if (!file) return "Please select committee meeting photo.";

  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    return "Only JPG, PNG or WEBP committee photos are allowed.";
  }

  if (file.size > MAX_PHOTO_SIZE) {
    return "Committee photo size cannot exceed 8 MB.";
  }

  return "";
};

const validateLetterhead = (file) => {
  if (!file) return "Please select the PACS letterhead image.";
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    return "Upload the full A4 PACS letterhead as JPG, PNG or WEBP image.";
  }
  if (file.size > MAX_LETTERHEAD_SIZE) {
    return "Letterhead image size cannot exceed 10 MB.";
  }
  return "";
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Letterhead image could not be read."));
    reader.readAsDataURL(file);
  });

const imageFromDataUrl = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Letterhead image is invalid."));
    image.src = dataUrl;
  });

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/*
  Detects the occupied header/footer bands on a full A4 letterhead image.
  The detected margins remain editable because letterheads with watermarks or
  unusual side artwork may need a small manual correction before generation.
*/
const inspectLetterheadImage = async (file) => {
  const previewDataUrl = await fileToDataUrl(file);
  const image = await imageFromDataUrl(previewDataUrl);
  const width = Math.min(1000, Math.max(400, image.naturalWidth || 1000));
  const height = Math.max(
    560,
    Math.round(width * ((image.naturalHeight || 1414) / (image.naturalWidth || 1000))),
  );
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  const occupiedRow = (row) => {
    let ink = 0;
    for (let column = 0; column < width; column += 2) {
      const index = (row * width + column) * 4;
      const alpha = pixels[index + 3];
      const brightness = (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
      if (alpha > 30 && brightness < 244) ink += 1;
    }
    return ink >= width * 0.008;
  };
  let headerBottom = Math.round(height * 0.12);
  for (let row = 0; row < Math.round(height * 0.42); row += 1) {
    if (occupiedRow(row)) headerBottom = row;
  }
  let footerTop = Math.round(height * 0.92);
  for (let row = Math.round(height * 0.64); row < height; row += 1) {
    if (occupiedRow(row)) {
      footerTop = row;
      break;
    }
  }
  canvas.width = 1;
  canvas.height = 1;
  return {
    previewDataUrl,
    width: image.naturalWidth || width,
    height: image.naturalHeight || height,
    topSafeMm: Math.round(clamp((headerBottom / height) * 297 + 7, 28, 85)),
    bottomSafeMm: Math.round(
      clamp(((height - footerTop) / height) * 297 + 7, 16, 58),
    ),
    leftSafeMm: 17,
    rightSafeMm: 17,
  };
};

export default function PacsDocumentCenter({
  profile = {},
  project = {},
  onSaved,
}) {
  const existingGenerated = project.generatedDocuments || {};
  const existingSigned = project.signedPacsDocuments || {};
  const existingLetterhead = project.pacsLetterhead || {
    url: project.letterheadUrl || project.pacsLetterheadUrl || "",
    storagePath:
      project.letterheadStoragePath || project.pacsLetterheadStoragePath || "",
    topSafeMm: project.letterheadTopSafeMm || 46,
    bottomSafeMm: project.letterheadBottomSafeMm || 24,
    leftSafeMm: project.letterheadLeftSafeMm || 17,
    rightSafeMm: project.letterheadRightSafeMm || 17,
    version: project.letterheadVersion || 0,
  };

  const archived =
    project.isDeleted === true ||
    project.deleted === true ||
    project.archived === true ||
    project.isArchived === true ||
    ["DELETED", "ARCHIVED", "DELETED_FROM_PMC_ACTIVE_LIST"].includes(
      String(project.archiveStatus || "").toUpperCase(),
    );

  const [form, setForm] = useState({
    resolutionNumber:
      project.resolutionNumber ||
      project.resolutionNo ||
      project.committeeResolutionNo ||
      project.managingCommitteeResolutionNo ||
      "",
    resolutionDate:
      project.resolutionDate ||
      project.committeeResolutionDate ||
      project.managingCommitteeResolutionDate ||
      today(),
    meetingTime: project.meetingTime || "",
    meetingPlace:
      project.meetingPlace || project.village || profile.village || "",
    agendaNumber: project.agendaNumber || "",
    totalCommitteeMembers: project.totalCommitteeMembers || "",
    membersPresent: project.membersPresent || "",
    quorumStatus: project.quorumStatus || "Quorum duly constituted",

    chairmanName: project.chairmanName || "",
    secretaryName: project.secretaryName || profile.name || "",

    capacityMt:
      numericCapacity(
        project.proposedCapacityMt,
        project.capacityMt,
        project.capacity,
        project.godownCapacity,
        project.selectedCapacity,
        profile.proposedCapacityMt,
        profile.capacityMt,
      ) || "",

    landOwnershipType:
      project.landLeaseDetails?.landOwnershipType ||
      project.landOwnershipType ||
      project.landOwnership ||
      "OWNED",

    leaseYears:
      project.landLeaseDetails?.leaseYears || project.leaseYears || 20,
    contributionRate: project.contributionRate || 2000,

    ownContribution: project.ownContribution || "",

    bankFinanceRequired: project.bankFinanceRequired || "",

    latestAuditYear: project.latestAuditYear || "",

    auditClassification: project.auditClassification || "",

    cooperativeAct: project.cooperativeAct || project.registrationAct || "",

    documentStatus: "DRAFT",
  });

  const [letterhead, setLetterhead] = useState({
    ...existingLetterhead,
    topSafeMm: Number(existingLetterhead.topSafeMm || 46),
    bottomSafeMm: Number(existingLetterhead.bottomSafeMm || 24),
    leftSafeMm: Number(existingLetterhead.leftSafeMm || 17),
    rightSafeMm: Number(existingLetterhead.rightSafeMm || 17),
  });
  const [letterheadFile, setLetterheadFile] = useState(null);

  const [generated, setGenerated] = useState(existingGenerated);

  const [signedDocuments, setSignedDocuments] = useState(existingSigned);

  const [signedFiles, setSignedFiles] = useState({});
  const [photo, setPhoto] = useState(null);
  const [savedEvidence, setSavedEvidence] = useState(
    project.committeeEvidence || null,
  );
  const [activeStep, setActiveStep] = useState(() => {
    const generatedReady = PACS_DOCUMENT_TYPES.every(
      (item) => existingGenerated[item.id]?.url,
    );
    const generatedForSavedLetterhead =
      generatedReady &&
      Boolean(clean(existingLetterhead.url)) &&
      PACS_DOCUMENT_TYPES.every(
        (item) =>
          Number(existingGenerated[item.id]?.letterheadVersion || 0) ===
          Number(existingLetterhead.version || 0),
      );
    if (generatedReady && !generatedForSavedLetterhead) return 2;
    if (generatedForSavedLetterhead) {
      if (!REQUIRE_MEETING_EVIDENCE) return 4;
      return project.committeeEvidence?.photoUrl ? 4 : 3;
    }
    return 1;
  });

  const [gps, setGps] = useState(
    project.committeeEvidence
      ? {
          latitude: project.committeeEvidence.latitude,
          longitude: project.committeeEvidence.longitude,
          accuracy: project.committeeEvidence.accuracy,
          capturedAt: project.committeeEvidence.capturedAt,
        }
      : null,
  );

  const [busy, setBusy] = useState("");
  const [gpsPolicyBlocked, setGpsPolicyBlocked] = useState(false);
  const [documentLanguage, setDocumentLanguage] = useState("GUJARATI");
  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const reviewInProgress = [
    "PMC_UNDER_REVIEW",
    "PMC_DOCUMENTS_APPROVED",
    "MOU_PENDING_PACS_SIGNATURE",
    "PENDING_PMC_SIGNATURE",
    "PENDING_NAFED_SIGNATURE",
  ].includes(project.documentStage);

  // Review-stage records remain editable through a new audited version.
  // Only archived and fully executed records are permanently locked.
  const locked = archived || project.documentStage === "FULLY_EXECUTED";

  const base = useMemo(() => {
    const organizationName = firstValue(
      project.societyName,
      project.pacsName,
      project.organizationName,
      project.registeredSocietyName,
      project.registeredName,
      project.applicantSocietyName,
      profile.organizationName,
      profile.societyName,
      profile.pacsName,
      profile.registeredSocietyName,
      profile.registeredName,
    );

    const village = firstValue(
      project.village,
      project.pacsVillage,
      project.location?.village,
      project.addressDetails?.village,
      profile.village,
      profile.pacsVillage,
      profile.location?.village,
      profile.addressDetails?.village,
    );

    const taluka = firstValue(
      project.taluka,
      project.block,
      project.tehsil,
      project.pacsTaluka,
      project.location?.taluka,
      project.addressDetails?.taluka,
      profile.taluka,
      profile.block,
      profile.tehsil,
      profile.pacsTaluka,
      profile.location?.taluka,
      profile.addressDetails?.taluka,
    );

    const district = firstValue(
      project.district,
      project.pacsDistrict,
      project.location?.district,
      project.addressDetails?.district,
      profile.district,
      profile.pacsDistrict,
      profile.location?.district,
      profile.addressDetails?.district,
    );

    return {
      ...profile,
      ...project,
      ...form,

      // Exact registered PACS name. Never append a fixed suffix here.
      organizationName,
      societyName: organizationName,
      pacsName: organizationName,

      registrationNumber:
        project.registrationNumber ||
        profile.registrationNumber ||
        profile.registrationNo,

      registrationAct: project.registrationAct || profile.registrationAct,

      registrationDate: project.registrationDate || profile.registrationDate,

      address: project.address || profile.address,

      state: project.state || profile.state,

      district,
      taluka,
      village,

      capacityMt: numericCapacity(
        form.capacityMt,
        project.proposedCapacityMt,
        project.capacityMt,
        project.capacity,
        project.godownCapacity,
      ),

      applicationId: project.applicationId || project.projectId || project.id,

      generatedDate: today(),

      // Normalise every signature/seal field used by the existing PACS
      // dashboard so the official drafts can reuse the saved assets.
      chairmanSignatureUrl: firstValue(
        project.chairmanSignatureUrl,
        project.chairpersonSignatureUrl,
        project.authorisedSignatures?.chairman?.url,
        project.authorisedSignatures?.chairperson?.url,
        project.pacsSignatures?.chairman?.url,
        project.pacsSignatures?.chairperson?.signatureUrl,
        project.mouSignatures?.pacs?.chairperson?.signatureUrl,
        profile.chairmanSignatureUrl,
        profile.chairpersonSignatureUrl,
        profile.authorisedSignatures?.chairman?.url,
        profile.authorisedSignatures?.chairperson?.url,
      ),

      secretarySignatureUrl: firstValue(
        project.secretarySignatureUrl,
        project.authorisedSignatures?.secretary?.url,
        project.pacsSignatures?.secretary?.url,
        project.pacsSignatures?.secretary?.signatureUrl,
        project.mouSignatures?.pacs?.secretary?.signatureUrl,
        profile.secretarySignatureUrl,
        profile.authorisedSignatures?.secretary?.url,
      ),

      societySealUrl: firstValue(
        project.societySealUrl,
        project.officialSealUrl,
        project.societyStampUrl,
        project.stampUrl,
        project.authorisedSignatures?.seal?.url,
        project.authorisedSignatures?.stamp?.url,
        project.pacsSignatures?.seal?.url,
        project.pacsSignatures?.stamp?.url,
        profile.societySealUrl,
        profile.officialSealUrl,
        profile.societyStampUrl,
        profile.stampUrl,
        profile.authorisedSignatures?.seal?.url,
        profile.authorisedSignatures?.stamp?.url,
      ),
    };
  }, [profile, project, form]);

  const generatedCount = PACS_DOCUMENT_TYPES.filter(
    (item) => generated[item.id]?.url,
  ).length;

  const signedCount = PACS_DOCUMENT_TYPES.filter(
    (item) => signedDocuments[item.id]?.url,
  ).length;

  const draftsUseCurrentLetterhead =
    Boolean(clean(letterhead.url)) &&
    PACS_DOCUMENT_TYPES.every(
      (item) =>
        generated[item.id]?.url &&
        Number(generated[item.id]?.letterheadVersion || 0) ===
          Number(letterhead.version || 0),
    );

  const committeeEvidenceAvailable = Boolean(
    savedEvidence?.photoUrl || (photo && gps),
  );

  const showMessage = (text, type = "info") => {
    setMessage({ text, type });

    window.setTimeout(() => {
      setMessage((current) =>
        current.text === text ? { type: "", text: "" } : current,
      );
    }, 8000);
  };

  const selectLetterhead = async (file) => {
    if (!file) {
      setLetterheadFile(null);
      return;
    }
    const error = validateLetterhead(file);
    if (error) return showMessage(error, "error");
    try {
      setBusy("inspect-letterhead");
      const inspection = await inspectLetterheadImage(file);
      setLetterheadFile(file);
      setLetterhead((current) => ({
        ...current,
        ...inspection,
        fileName: file.name,
        contentType: file.type,
      }));
      showMessage(
        "Letterhead detected. Verify the preview and safe margins, then save it.",
        "success",
      );
    } catch (error) {
      showMessage(error.message || "Letterhead could not be inspected.", "error");
    } finally {
      setBusy("");
    }
  };

  const changeLetterheadMargin = (key, value) => {
    const limits = {
      topSafeMm: [22, 88],
      bottomSafeMm: [12, 60],
      leftSafeMm: [10, 35],
      rightSafeMm: [10, 35],
    };
    const [min, max] = limits[key] || [0, 100];
    setLetterhead((current) => ({
      ...current,
      [key]: clamp(Number(value || min), min, max),
    }));
  };

  const saveLetterhead = async () => {
    const user = auth.currentUser;
    if (!user || !project.id)
      return showMessage("Application or login session is unavailable.", "error");
    if (locked) return showMessage("This application is locked.", "error");
    if (!letterheadFile && !clean(letterhead.url))
      return showMessage("Select the PACS letterhead image first.", "error");

    try {
      setBusy("save-letterhead");
      const replacingAfterGeneration = Boolean(
        letterheadFile &&
          PACS_DOCUMENT_TYPES.some((item) => generated[item.id]?.url),
      );
      const version = Number(letterhead.version || 0) + (letterheadFile ? 1 : 0);
      let url = clean(letterhead.url);
      let storagePath = clean(letterhead.storagePath);
      if (letterheadFile) {
        storagePath =
          `nafed_pacs/${user.uid}/${project.id}/letterhead/` +
          `v${version}_${Date.now()}_${safeFileName(letterheadFile.name)}`;
        const objectRef = ref(storage, storagePath);
        await uploadBytes(objectRef, letterheadFile, {
          contentType: letterheadFile.type,
          customMetadata: {
            projectId: project.id,
            applicationId: clean(base.applicationId),
            documentType: "PACS_OFFICIAL_LETTERHEAD",
            version: String(version),
            uploadedByUid: user.uid,
          },
        });
        url = await getDownloadURL(objectRef);
      }

      const uploadedAtIso = new Date().toISOString();
      const record = {
        url,
        storagePath,
        fileName: letterheadFile?.name || letterhead.fileName || "PACS Letterhead",
        contentType: letterheadFile?.type || letterhead.contentType || "image/png",
        width: Number(letterhead.width || 0),
        height: Number(letterhead.height || 0),
        topSafeMm: Number(letterhead.topSafeMm || 46),
        bottomSafeMm: Number(letterhead.bottomSafeMm || 24),
        leftSafeMm: Number(letterhead.leftSafeMm || 17),
        rightSafeMm: Number(letterhead.rightSafeMm || 17),
        version,
        status: "ACTIVE",
        uploadedAtIso,
        uploadedByUid: user.uid,
      };
      const update = {
        pacsLetterhead: record,
        letterheadUrl: url,
        letterheadStoragePath: storagePath,
        letterheadTopSafeMm: record.topSafeMm,
        letterheadBottomSafeMm: record.bottomSafeMm,
        letterheadLeftSafeMm: record.leftSafeMm,
        letterheadRightSafeMm: record.rightSafeMm,
        letterheadVersion: version,
        letterheadUpdatedAtIso: uploadedAtIso,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      await recordActivity(
        "PACS_PROJECT_AND_MEETING_DETAILS_UPDATED",
        project.documentStage || "PACS_DOCUMENT_PREPARATION",
        { letterheadUpdated: true, letterheadVersion: version },
      );
      setLetterhead(record);
      setLetterheadFile(null);
      onSaved?.(update);
      if (replacingAfterGeneration) {
        setActiveStep(2);
        showMessage(
          "New letterhead saved. Regenerate all five signed-and-stamped PDFs before PMC submission.",
          "success",
        );
      } else {
        showMessage(
          "Official PACS letterhead saved. All five documents will now adjust inside its safe area.",
          "success",
        );
      }
    } catch (error) {
      console.error("PACS letterhead upload failed:", error);
      showMessage(error.message || "Letterhead could not be saved.", "error");
    } finally {
      setBusy("");
    }
  };

  const recordActivity = async (action, newStage, extra = {}) => {
    const user = auth.currentUser;
    if (!user || !project.id) return;

    try {
      const activityId = `${project.id}_${action}_${Date.now()}`;
      await setDoc(doc(db, "nafed_activity", activityId), {
        action,
        entityType: "GODOWN_PROJECT",
        entityId: project.id,
        projectId: project.id,
        applicationId: clean(base.applicationId),
        pacsUid: user.uid,
        performedByUid: user.uid,
        performedByEmail: user.email || "",
        performedByRole: "PACS",
        previousStage: project.documentStage || "PACS_DOCUMENT_PREPARATION",
        newStage,
        ...extra,
        createdAt: serverTimestamp(),
        createdAtIso: new Date().toISOString(),
      });
    } catch (error) {
      console.warn("Document activity log could not be written:", error);
    }
  };

  const deleteStorageFile = async (record = {}) => {
    const storagePath = clean(record.storagePath || record.photoStoragePath);

    if (!storagePath) return;

    try {
      await deleteObject(ref(storage, storagePath));
    } catch (error) {
      // A missing object already has the desired Storage state. Firestore
      // cleanup should still continue so a broken link is not retained.
      if (error?.code !== "storage/object-not-found") {
        throw error;
      }
    }
  };

  const stageAfterDocumentRemoval = (nextGeneratedCount) => {
    if (nextGeneratedCount === PACS_DOCUMENT_TYPES.length) {
      return {
        documentStage: "PACS_DRAFT_DOCUMENTS_GENERATED",
        currentStage: "Signed-and-Stamped PDFs Ready for PMC Submission",
        documentCenterStep: 2,
        documentCenterProgress: 50,
      };
    }

    if (nextGeneratedCount > 0) {
      return {
        documentStage: "PACS_DRAFT_DOCUMENTS_PARTIAL",
        currentStage: "Draft Documents Incomplete",
        documentCenterStep: 2,
        documentCenterProgress: 30,
      };
    }

    return {
      documentStage: "PACS_PROJECT_DETAILS_COMPLETED",
      currentStage: "Project Details Completed",
      documentCenterStep: 1,
      documentCenterProgress: 20,
    };
  };

  const deletePacsDocument = async (kind, type) => {
    const user = auth.currentUser;
    const item = PACS_DOCUMENT_TYPES.find(
      (documentType) => documentType.id === type,
    );
    const record =
      kind === "generated" ? generated[type] : signedDocuments[type];

    if (!user || !project.id) {
      return showMessage(
        "Application or login session is not available.",
        "error",
      );
    }

    const policy = canDeletePacsDocumentRecord({
      project,
      record,
      kind,
      currentUid: user.uid,
    });

    if (!policy.allowed) {
      return showMessage(policy.reason, "error");
    }

    if (kind === "generated" && signedDocuments[type]?.url) {
      return showMessage(
        "Delete this document's signed copy first, then delete the draft.",
        "error",
      );
    }

    const sharedSignedStorage =
      kind === "signed" &&
      clean(record?.storagePath) &&
      clean(record?.storagePath) === clean(generated[type]?.storagePath);

    const confirmation = window.confirm(
      `Permanently delete "${item?.title || record?.title || "document"}"?` +
        (sharedSignedStorage
          ? "\n\nThis signed PDF is also the current generated PDF, so both entries will be removed."
          : "") +
        "\n\nThis action cannot be undone.",
    );

    if (!confirmation) return;

    const deletedAtIso = new Date().toISOString();
    const audit = buildPacsDocumentDeletionAudit({
      project,
      record,
      kind,
      documentType: type,
      title: item?.title,
      deletedByUid: user.uid,
      deletedByEmail: user.email || "",
      deletedAtIso,
    });

    try {
      setBusy(`delete-${kind}-${type}`);
      setMessage({ type: "", text: "" });

      await deleteStorageFile(record);

      const nextGenerated = { ...generated };
      const nextSigned = { ...signedDocuments };

      if (kind === "generated" || sharedSignedStorage) {
        delete nextGenerated[type];
      }

      if (kind === "signed") {
        delete nextSigned[type];
      }

      const nextGeneratedCount = PACS_DOCUMENT_TYPES.filter(
        (documentType) => nextGenerated[documentType.id]?.url,
      ).length;
      const nextSignedCount = PACS_DOCUMENT_TYPES.filter(
        (documentType) => nextSigned[documentType.id]?.url,
      ).length;
      const nextStage = stageAfterDocumentRemoval(
        nextGeneratedCount,
        nextSignedCount,
      );

      await updateDoc(doc(db, "godown_projects", project.id), {
        generatedDocuments: nextGenerated,
        signedPacsDocuments: nextSigned,
        documentsGeneratedCount: nextGeneratedCount,
        signedDocumentsUploadedCount: nextSignedCount,
        ...nextStage,
        documentDeletionHistory: arrayUnion(audit),
        lastDocumentDeletedAt: serverTimestamp(),
        lastDocumentDeletedAtIso: deletedAtIso,
        lastDocumentDeletedByUid: user.uid,
        lastDocumentDeletedByEmail: user.email || "",
        updatedAt: serverTimestamp(),
      });

      await recordActivity(
        kind === "generated"
          ? "PACS_DRAFT_DOCUMENT_DELETED"
          : "PACS_SIGNED_DOCUMENT_DELETED",
        nextStage.documentStage,
        {
          documentType: type,
          documentTitle: item?.title || record?.title || "",
          deletedStoragePath: audit.storagePath,
          sharedGeneratedRecordDeleted: sharedSignedStorage,
          deletionAudit: audit,
        },
      );

      setGenerated(nextGenerated);
      setSignedDocuments(nextSigned);
      setSignedFiles((current) => {
        const updated = { ...current };
        delete updated[type];
        return updated;
      });
      setActiveStep(nextStage.documentCenterStep);

      onSaved?.({
        generatedDocuments: nextGenerated,
        signedPacsDocuments: nextSigned,
        documentsGeneratedCount: nextGeneratedCount,
        signedDocumentsUploadedCount: nextSignedCount,
        ...nextStage,
      });

      showMessage(
        `${kind === "generated" ? "Draft" : "Signed"} document deleted successfully.`,
        "success",
      );
    } catch (error) {
      console.error("PACS document deletion failed:", error);
      showMessage(
        error?.code === "storage/unauthorized"
          ? "Delete permission is missing. Deploy the included Firebase Storage Rules update."
          : error.message || "Document could not be deleted.",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const deleteCommitteeEvidence = async () => {
    const user = auth.currentUser;

    if (!user || !project.id) {
      return showMessage(
        "Application or login session is not available.",
        "error",
      );
    }

    const policy = canDeletePacsDocumentRecord({
      project,
      record: savedEvidence || {},
      kind: "evidence",
      currentUid: user.uid,
    });

    if (!policy.allowed) {
      return showMessage(policy.reason, "error");
    }

    if (
      !window.confirm(
        "Permanently delete the committee photo and its saved GPS evidence?\n\nThis action cannot be undone.",
      )
    ) {
      return;
    }

    const deletedAtIso = new Date().toISOString();
    const audit = buildPacsDocumentDeletionAudit({
      project,
      record: savedEvidence,
      kind: "evidence",
      documentType: "COMMITTEE_EVIDENCE",
      title: "Committee Meeting Photo & GPS Evidence",
      deletedByUid: user.uid,
      deletedByEmail: user.email || "",
      deletedAtIso,
    });
    const nextStage = stageAfterDocumentRemoval(generatedCount, signedCount);

    try {
      setBusy("delete-evidence");
      setMessage({ type: "", text: "" });

      await deleteStorageFile(savedEvidence);

      await updateDoc(doc(db, "godown_projects", project.id), {
        committeeEvidence: null,
        committeePhotoUrl: "",
        committeeVerificationStatus: "NOT_UPLOADED",
        latitude: null,
        longitude: null,
        ...nextStage,
        documentDeletionHistory: arrayUnion(audit),
        lastDocumentDeletedAt: serverTimestamp(),
        lastDocumentDeletedAtIso: deletedAtIso,
        lastDocumentDeletedByUid: user.uid,
        lastDocumentDeletedByEmail: user.email || "",
        updatedAt: serverTimestamp(),
      });

      await recordActivity(
        "PACS_COMMITTEE_EVIDENCE_DELETED",
        nextStage.documentStage,
        {
          documentType: "COMMITTEE_EVIDENCE",
          deletedStoragePath: audit.storagePath,
          deletionAudit: audit,
        },
      );

      setSavedEvidence(null);
      setPhoto(null);
      setActiveStep(REQUIRE_MEETING_EVIDENCE ? 3 : 4);

      onSaved?.({
        committeeEvidence: null,
        committeePhotoUrl: "",
        committeeVerificationStatus: "NOT_UPLOADED",
        latitude: null,
        longitude: null,
        ...nextStage,
      });

      showMessage(
        "Committee photo and GPS evidence deleted successfully.",
        "success",
      );
    } catch (error) {
      console.error("Committee evidence deletion failed:", error);
      showMessage(
        error?.code === "storage/unauthorized"
          ? "Delete permission is missing. Deploy the included Firebase Storage Rules update."
          : error.message || "Committee evidence could not be deleted.",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const change = (key, value) => {
    if (locked) return;

    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const validateForm = ({ requireLetterhead = false } = {}) => {
    if (requireLetterhead && !clean(letterhead.url)) {
      return "Upload and save the official PACS letterhead before generating documents.";
    }
    if (!clean(base.organizationName)) {
      return "Registered PACS/Society name is missing in the application profile.";
    }

    if (!clean(base.village)) {
      return "PACS village is missing in the application profile.";
    }

    if (!clean(base.taluka)) {
      return "PACS taluka/block is missing in the application profile.";
    }

    if (!clean(base.district)) {
      return "PACS district is missing in the application profile.";
    }

    if (!clean(form.resolutionNumber)) {
      return "Enter Managing Committee Resolution Number.";
    }

    if (!form.resolutionDate) {
      return "Select Managing Committee Resolution Date.";
    }

    if (!clean(form.meetingTime)) {
      return "Enter committee meeting time.";
    }

    if (!clean(form.meetingPlace)) {
      return "Enter committee meeting venue.";
    }

    if (!clean(form.agendaNumber)) {
      return "Enter agenda item number.";
    }

    if (
      !Number(form.totalCommitteeMembers) ||
      Number(form.totalCommitteeMembers) < 1
    ) {
      return "Enter total Managing Committee members.";
    }

    if (!Number(form.membersPresent) || Number(form.membersPresent) < 1) {
      return "Enter number of committee members present.";
    }

    if (Number(form.membersPresent) > Number(form.totalCommitteeMembers)) {
      return "Members present cannot exceed total committee members.";
    }

    if (!clean(form.quorumStatus)) {
      return "Confirm the committee meeting quorum status.";
    }

    if (!clean(form.chairmanName)) {
      return "Enter PACS Chairman name.";
    }

    if (!clean(form.secretaryName)) {
      return "Enter Secretary/CEO name.";
    }

    if (!Number(base.capacityMt) || Number(base.capacityMt) <= 0) {
      return "Enter valid godown capacity.";
    }

    if (form.landOwnershipType === "LEASED" && Number(form.leaseYears) < 20) {
      return "Registered lease period must satisfy the applicable Scheme requirement.";
    }

    if (!Number(form.contributionRate) || Number(form.contributionRate) <= 0) {
      return "Enter valid contribution rate per MT.";
    }

    return "";
  };

  const saveProjectDetails = async () => {
    const user = auth.currentUser;

    if (!user || !project.id) {
      return showMessage(
        "Application or login session is not available.",
        "error",
      );
    }

    if (locked) {
      return showMessage(
        "Fully executed or archived records cannot be edited.",
        "error",
      );
    }

    const validationError = validateForm();
    if (validationError) return showMessage(validationError, "error");

    try {
      setBusy("save-details");
      const updatedAtIso = new Date().toISOString();
      const nextStage = reviewInProgress
        ? project.documentStage
        : "PACS_PROJECT_DETAILS_COMPLETED";

      const details = {
        ...form,
        resolutionNo: clean(form.resolutionNumber),
        committeeResolutionNo: clean(form.resolutionNumber),
        managingCommitteeResolutionNo: clean(form.resolutionNumber),
        committeeResolutionDate: form.resolutionDate,
        managingCommitteeResolutionDate: form.resolutionDate,
        societyName: clean(base.organizationName),
        pacsName: clean(base.organizationName),
        organizationName: clean(base.organizationName),
        village: clean(base.village),
        taluka: clean(base.taluka),
        district: clean(base.district),
        capacityMt: Number(base.capacityMt),
        leaseYears: Number(form.leaseYears || 0),
        contributionRate: Number(form.contributionRate || 0),
        totalCommitteeMembers: Number(form.totalCommitteeMembers),
        membersPresent: Number(form.membersPresent),
        ownContribution: Number(form.ownContribution || 0),
        bankFinanceRequired: Number(form.bankFinanceRequired || 0),
        projectDetailsCompleted: true,
        projectDetailsUpdatedAtIso: updatedAtIso,
        projectDetailsUpdatedByUid: user.uid,
        documentStage: nextStage,
        currentStage: reviewInProgress
          ? "Details Updated – Document Regeneration Required"
          : "Project Details Completed",
        documentCenterStep: reviewInProgress ? 2 : 1,
        documentCenterProgress: reviewInProgress ? 30 : 20,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "godown_projects", project.id), details);

      await recordActivity(
        "PACS_PROJECT_AND_MEETING_DETAILS_UPDATED",
        nextStage,
        {
          resolutionNumber: clean(form.resolutionNumber),
          resolutionDate: form.resolutionDate,
          meetingTime: form.meetingTime,
          capacityMt: Number(base.capacityMt),
          updatedDuringReview: reviewInProgress,
        },
      );

      if (reviewInProgress) {
        await setDoc(
          doc(db, "notifications_pmc", `${project.id}_DETAILS_UPDATED`),
          {
            type: "NAFED_PACS_DOCUMENT_DETAILS_UPDATED",
            title: "PACS Document Details Updated",
            message: `${clean(base.organizationName)} updated committee/project details. A new document version will be generated.`,
            projectId: project.id,
            applicationId: clean(base.applicationId),
            pacsId: user.uid,
            pacsName: clean(base.organizationName),
            status: "UNREAD",
            priority: "HIGH",
            createdAt: serverTimestamp(),
            createdAtIso: updatedAtIso,
          },
          { merge: true },
        );
      }

      onSaved?.(details);
      setActiveStep(2);
      showMessage(
        "Meeting and project details saved successfully. Generate the updated professional documents now.",
        "success",
      );
    } catch (error) {
      console.error("PACS details save failed:", error);
      showMessage(
        error.message || "Meeting and project details could not be saved.",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const generateOne = async (type, action = "PREVIEW") => {
    try {
      const validationError = validateForm({ requireLetterhead: true });

      if (validationError) {
        return showMessage(validationError, "error");
      }

      const item = PACS_DOCUMENT_TYPES.find((entry) => entry.id === type);
      const html = buildPacsBilingualDocumentHtml(
        type,
        bilingualDocumentData,
        documentLanguage,
      );
      if (action === "PREVIEW") return openOfficialPreview(html);
      setBusy(`official-${type}-${documentLanguage}`);
      await downloadOfficialPdf(
        html,
        `${item?.shortCode || type}_${safeFilePart(
          base.applicationId,
        )}_${documentLanguage}.pdf`,
      );
      showMessage(`${documentLanguage} PDF downloaded successfully.`, "success");
    } catch (error) {
      console.error(error);
      showMessage(error.message || "Official PDF could not be generated.", "error");
    } finally {
      setBusy("");
    }
  };

  const generateAndUploadAll = async () => {
    const user = auth.currentUser;

    if (!user) {
      return showMessage(
        "Your login session has expired. Please login again.",
        "error",
      );
    }

    if (!project.id) {
      return showMessage(
        "Submit the godown application before generating documents.",
        "error",
      );
    }

    if (locked) {
      return showMessage(
        "Fully executed or archived documents cannot be regenerated.",
        "error",
      );
    }

    const validationError = validateForm({ requireLetterhead: true });

    if (validationError) {
      return showMessage(validationError, "error");
    }

    try {
      setBusy("generate");
      setMessage({ type: "", text: "" });

      const uploaded = {};
      const versionHistory = [];
      const generatedAtIso = new Date().toISOString();
      const generationStage = reviewInProgress
        ? project.documentStage
        : "PACS_DRAFT_DOCUMENTS_GENERATED";
      const generationCurrentStage = reviewInProgress
        ? "Updated Signed-and-Stamped PDFs Under PMC Review"
        : "PACS Documents Generated";

      for (const item of PACS_DOCUMENT_TYPES) {
        const currentVersion = Number(generated[item.id]?.version || 0) + 1;
        const html = buildPacsBilingualDocumentHtml(
          item.id,
          { ...bilingualDocumentData, generatedDate: generatedAtIso },
          documentLanguage,
        );
        const blob = await officialHtmlToPdfBlob(
          html,
          `${item.shortCode}_${documentLanguage}.pdf`,
        );
        const fileName = `${item.shortCode}_${safeFilePart(
          base.applicationId,
        )}_${documentLanguage}_V${currentVersion}.pdf`;
        const documentNumber = `${safeFilePart(
          base.applicationId,
        )}-${item.shortCode}-V${currentVersion}`;
        const hash = await sha256(blob);

        const storagePath =
          `nafed_pacs/${user.uid}/${project.id}` +
          `/generated_documents/${item.id}` +
          `/v${currentVersion}_${fileName}`;

        const fileRef = ref(storage, storagePath);

        await uploadBytes(fileRef, blob, {
          contentType: "application/pdf",
          customMetadata: {
            projectId: project.id,
            applicationId: clean(base.applicationId),
            documentType: item.id,
            language: documentLanguage,
            letterheadVersion: String(letterhead.version || 1),
            version: String(currentVersion),
            generatedByUid: user.uid,
            sha256: hash || "NOT_AVAILABLE",
          },
        });

        const url = await getDownloadURL(fileRef);

        const record = {
          type: item.id,
          shortCode: item.shortCode,
          title: item.title,
          documentNumber,
          fileName,
          storagePath,
          url,
          sha256: hash,
          version: currentVersion,
          language: documentLanguage,
          letterheadVersion: Number(letterhead.version || 1),
          letterheadUrl: letterhead.url,
          status: "SIGNED_AND_STAMPED_PDF_GENERATED",
          generatedAtIso,
          generatedByUid: user.uid,
          generatedByEmail: user.email || "",
        };

        uploaded[item.id] = record;
        versionHistory.push(record);
      }

      await updateDoc(doc(db, "godown_projects", project.id), {
        ...form,

        societyName: clean(base.organizationName),
        pacsName: clean(base.organizationName),
        organizationName: clean(base.organizationName),
        village: clean(base.village),
        taluka: clean(base.taluka),
        district: clean(base.district),

        capacityMt: Number(base.capacityMt),
        leaseYears: Number(form.leaseYears || 0),
        contributionRate: Number(form.contributionRate),

        totalCommitteeMembers: Number(form.totalCommitteeMembers),

        membersPresent: Number(form.membersPresent),

        generatedDocuments: uploaded,

        // Retire legacy separately-uploaded signed copies. The generated PDFs
        // now already contain the saved signatures and society seal.
        signedPacsDocuments: {},
        signedDocumentsUploadedCount: 0,

        generatedDocumentHistory: arrayUnion(...versionHistory),

        documentsGeneratedCount: PACS_DOCUMENT_TYPES.length,

        documentStage: generationStage,

        currentStage: generationCurrentStage,

        documentCenterStep: 2,
        documentCenterProgress: 50,

        updatedAt: serverTimestamp(),
      });

      await recordActivity(
        "PACS_DRAFT_DOCUMENT_SET_GENERATED",
        generationStage,
        {
          documentCount: PACS_DOCUMENT_TYPES.length,
          regeneratedDuringReview: reviewInProgress,
        },
      );

      setGenerated(uploaded);
      setSignedDocuments({});
      setActiveStep(REQUIRE_MEETING_EVIDENCE ? 3 : 4);

      onSaved?.({
        ...form,
        generatedDocuments: uploaded,
        signedPacsDocuments: {},
        documentStage: generationStage,
        currentStage: generationCurrentStage,
        documentCenterStep: 2,
        documentCenterProgress: 50,
      });

      showMessage(
        `All five ${documentLanguage.toLowerCase()} signed-and-stamped PDFs have been generated and securely saved.`,
        "success",
      );
    } catch (error) {
      console.error("PACS document generation failed:", error);

      showMessage(
        error.message || "Document generation failed. Please try again.",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const selectSignedFile = (type, file) => {
    if (locked) return;

    if (!file) {
      setSignedFiles((current) => {
        const updated = { ...current };
        delete updated[type];
        return updated;
      });
      return;
    }

    const error = validatePdf(file);

    if (error) {
      return showMessage(error, "error");
    }

    setSignedFiles((current) => ({
      ...current,
      [type]: file,
    }));
  };

  const uploadSignedDocuments = async () => {
    const user = auth.currentUser;

    if (!user) {
      return showMessage("Your login session has expired.", "error");
    }

    if (!project.id) {
      return showMessage("Application is not available.", "error");
    }

    if (locked) {
      return showMessage(
        "Document set is locked and cannot be changed.",
        "error",
      );
    }

    if (generatedCount !== PACS_DOCUMENT_TYPES.length) {
      return showMessage(
        "Generate and save all five draft documents first.",
        "error",
      );
    }

    if (!draftsUseCurrentLetterhead) {
      setActiveStep(2);
      return showMessage(
        "Letterhead was added or changed. Regenerate all five drafts before uploading signed copies.",
        "error",
      );
    }

    const missing = PACS_DOCUMENT_TYPES.find(
      (item) => !signedFiles[item.id] && !signedDocuments[item.id]?.url,
    );

    if (missing) {
      return showMessage(`Upload signed PDF for: ${missing.title}`, "error");
    }

    try {
      setBusy("signed");
      setMessage({ type: "", text: "" });

      const uploaded = {
        ...signedDocuments,
      };

      const uploadedAtIso = new Date().toISOString();

      for (const item of PACS_DOCUMENT_TYPES) {
        const file = signedFiles[item.id];

        if (!file) continue;

        const hash = await sha256(file);
        const safeName = safeFileName(file.name);

        const signedVersion = Number(uploaded[item.id]?.signedVersion || 0) + 1;

        const storagePath =
          `nafed_pacs/${user.uid}/${project.id}` +
          `/signed_documents/${item.id}` +
          `/v${signedVersion}_${Date.now()}_${safeName}`;

        const fileRef = ref(storage, storagePath);

        await uploadBytes(fileRef, file, {
          contentType: "application/pdf",
          customMetadata: {
            projectId: project.id,
            applicationId: clean(base.applicationId),
            documentType: item.id,
            signedVersion: String(signedVersion),
            uploadedByUid: user.uid,
            sha256: hash || "NOT_AVAILABLE",
          },
        });

        const url = await getDownloadURL(fileRef);

        uploaded[item.id] = {
          type: item.id,
          title: item.title,
          fileName: file.name,
          storagePath,
          url,
          sha256: hash,
          size: file.size,
          signedVersion,
          draftVersion: generated[item.id]?.version || 1,
          status: "SIGNED_COPY_UPLOADED",
          verificationStatus: "PENDING_PMC_VERIFICATION",
          uploadedAtIso,
          uploadedByUid: user.uid,
          uploadedByEmail: user.email || "",
        };
      }

      await updateDoc(doc(db, "godown_projects", project.id), {
        signedPacsDocuments: uploaded,
        signedDocumentsUploadedCount: Object.keys(uploaded).length,

        documentStage: "PACS_SIGNED_DOCUMENTS_UPLOADED",

        currentStage: "Signed Documents Pending Submission",

        documentCenterStep: 3,
        documentCenterProgress: 60,

        updatedAt: serverTimestamp(),
      });

      await recordActivity(
        "PACS_SIGNED_DOCUMENT_SET_UPLOADED",
        "PACS_SIGNED_DOCUMENTS_UPLOADED",
        { documentCount: Object.keys(uploaded).length },
      );

      setSignedDocuments(uploaded);
      setSignedFiles({});
      setActiveStep(4);

      onSaved?.({
        signedPacsDocuments: uploaded,
        documentStage: "PACS_SIGNED_DOCUMENTS_UPLOADED",
        currentStage: "Signed Documents Pending Submission",
        documentCenterStep: 3,
        documentCenterProgress: 60,
      });

      showMessage(
        "All signed PACS documents have been uploaded successfully.",
        "success",
      );
    } catch (error) {
      console.error("Signed document upload failed:", error);

      showMessage(error.message || "Signed document upload failed.", "error");
    } finally {
      setBusy("");
    }
  };

  const locationPolicyAllowsGps = () => {
    try {
      const policy = document.permissionsPolicy || document.featurePolicy;
      return !policy?.allowsFeature || policy.allowsFeature("geolocation");
    } catch (error) {
      console.warn("Geolocation policy could not be inspected:", error);
      return true;
    }
  };

  const openGpsInDirectTab = () => {
    const directUrl = new URL(window.location.href);
    directUrl.searchParams.set("gps", "capture");
    const directWindow = window.open(directUrl.toString(), "_blank");

    if (!directWindow) {
      return showMessage(
        "New tab was blocked. Open this portal directly in Chrome/Edge and allow Location permission.",
        "error",
      );
    }

    try {
      directWindow.opener = null;
    } catch (error) {
      console.warn("New GPS tab opener could not be cleared:", error);
    }

    showMessage(
      "Portal opened directly. In the new tab, allow Location and press Capture Current GPS.",
      "success",
    );
  };

  const captureLocation = () => {
    setGpsPolicyBlocked(false);

    if (!window.isSecureContext) {
      setGpsPolicyBlocked(true);
      return showMessage(
        "Live GPS requires the secure HTTPS portal. Open the deployed HTTPS link directly.",
        "error",
      );
    }

    if (!locationPolicyAllowsGps()) {
      setGpsPolicyBlocked(true);
      return showMessage(
        "GPS is blocked inside this embedded window. Use Open Direct GPS Page below.",
        "error",
      );
    }

    if (!navigator.geolocation) {
      return showMessage("GPS is not supported on this device.", "error");
    }

    setBusy("gps");

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const location = {
          latitude: Number(coords.latitude.toFixed(7)),
          longitude: Number(coords.longitude.toFixed(7)),
          accuracy: Number(coords.accuracy.toFixed(2)),
          capturedAt: new Date().toISOString(),
        };

        setGps(location);
        setGpsPolicyBlocked(false);
        setBusy("");

        showMessage("Current GPS location captured successfully.", "success");
      },
      (error) => {
        setBusy("");

        const rawMessage = clean(error?.message).toLowerCase();
        const blockedByPolicy =
          !locationPolicyAllowsGps() ||
          rawMessage.includes("permissions policy") ||
          rawMessage.includes("disabled in this document");

        if (blockedByPolicy) {
          setGpsPolicyBlocked(true);
          return showMessage(
            "GPS is blocked in the current embedded window. Open the portal directly and capture again.",
            "error",
          );
        }

        const errorText =
          error?.code === 1
            ? "Location permission was denied. Allow Location for this site from browser settings and try again."
            : error?.code === 2
              ? "Current location is unavailable. Turn on device GPS and mobile data, then try again."
              : error?.code === 3
                ? "GPS request timed out. Move to an open area and capture again."
                : error?.message || "Current GPS location could not be captured.";

        showMessage(errorText, "error");
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      },
    );
  };

  const uploadGpsEvidence = async () => {
    const user = auth.currentUser;

    if (!user || !project.id) {
      return showMessage(
        "Application or login session is not available.",
        "error",
      );
    }

    if (locked) {
      return showMessage("Evidence is locked after PMC submission.", "error");
    }

    const photoError = validatePhoto(photo);

    if (photoError) {
      return showMessage(photoError, "error");
    }

    if (!gps) {
      return showMessage("Capture current GPS location first.", "error");
    }

    if (Number(gps.accuracy) > 100) {
      return showMessage(
        "GPS accuracy is too low. Move to an open area and capture location again.",
        "error",
      );
    }

    try {
      setBusy("evidence");

      const hash = await sha256(photo);
      const safeName = safeFileName(photo.name);

      const storagePath =
        `nafed_pacs/${user.uid}/${project.id}` +
        `/committee_evidence/${Date.now()}_${safeName}`;

      const fileRef = ref(storage, storagePath);

      await uploadBytes(fileRef, photo, {
        contentType: photo.type,
        customMetadata: {
          projectId: project.id,
          applicationId: clean(base.applicationId),
          latitude: String(gps.latitude),
          longitude: String(gps.longitude),
          accuracy: String(gps.accuracy),
          uploadedByUid: user.uid,
          sha256: hash || "NOT_AVAILABLE",
        },
      });

      const photoUrl = await getDownloadURL(fileRef);

      const evidence = {
        photoUrl,
        photoName: photo.name,
        photoStoragePath: storagePath,
        photoSha256: hash,
        photoSize: photo.size,

        latitude: gps.latitude,
        longitude: gps.longitude,
        accuracy: gps.accuracy,
        capturedAt: gps.capturedAt,

        meetingDate: form.resolutionDate,
        meetingTime: form.meetingTime,
        meetingPlace: form.meetingPlace,

        status: "PENDING_PMC_VERIFICATION",

        uploadedAtIso: new Date().toISOString(),

        uploadedByUid: user.uid,
        uploadedByEmail: user.email || "",
      };

      await updateDoc(doc(db, "godown_projects", project.id), {
        committeeEvidence: evidence,
        committeePhotoUrl: photoUrl,
        latitude: gps.latitude,
        longitude: gps.longitude,

        committeeVerificationStatus: "PENDING_PMC_VERIFICATION",

        documentStage: "COMMITTEE_EVIDENCE_UPLOADED",

        currentStage: "Document Set Ready for PMC Submission",

        documentCenterStep: 3,
        documentCenterProgress: 75,

        updatedAt: serverTimestamp(),
      });

      await recordActivity(
        "PACS_COMMITTEE_EVIDENCE_UPLOADED",
        "COMMITTEE_EVIDENCE_UPLOADED",
        {
          latitude: gps.latitude,
          longitude: gps.longitude,
          accuracy: gps.accuracy,
        },
      );

      setPhoto(null);
      setSavedEvidence(evidence);
      setActiveStep(4);

      onSaved?.({
        committeeEvidence: evidence,
        committeePhotoUrl: photoUrl,
        latitude: gps.latitude,
        longitude: gps.longitude,
        committeeVerificationStatus: "PENDING_PMC_VERIFICATION",
        documentStage: "COMMITTEE_EVIDENCE_UPLOADED",
        currentStage: "Document Set Ready for PMC Submission",
        documentCenterStep: 3,
        documentCenterProgress: 75,
      });

      showMessage(
        "Committee meeting photo and GPS evidence uploaded successfully.",
        "success",
      );
    } catch (error) {
      console.error("Committee evidence upload failed:", error);

      showMessage(
        error.message || "Committee evidence upload failed.",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const submitToPmc = async () => {
    const user = auth.currentUser;

    if (!user || !project.id) {
      return showMessage(
        "Application or login session is not available.",
        "error",
      );
    }

    if (locked) {
      return showMessage(
        "Documents have already been submitted to PMC.",
        "error",
      );
    }

    if (!clean(letterhead.url)) {
      setActiveStep(2);
      return showMessage("Save the official PACS letterhead first.", "error");
    }

    if (generatedCount !== PACS_DOCUMENT_TYPES.length) {
      return showMessage("Generate all five PACS documents first.", "error");
    }

    if (!draftsUseCurrentLetterhead) {
      setActiveStep(2);
      return showMessage(
        "The generated PDFs do not match the current letterhead. Regenerate all five PDFs first.",
        "error",
      );
    }

    if (REQUIRE_MEETING_EVIDENCE) {
      const evidenceLatitude = Number(savedEvidence?.latitude);
      const evidenceLongitude = Number(savedEvidence?.longitude);
      if (
        !savedEvidence?.photoUrl ||
        !Number.isFinite(evidenceLatitude) ||
        evidenceLatitude < -90 ||
        evidenceLatitude > 90 ||
        !Number.isFinite(evidenceLongitude) ||
        evidenceLongitude < -180 ||
        evidenceLongitude > 180
      ) {
        setActiveStep(3);
        return showMessage(
          "Upload the committee meeting photo with valid GPS coordinates before PMC submission.",
          "error",
        );
      }
    }

    const submittedDocuments = {};
    for (const item of PACS_DOCUMENT_TYPES) {
      const source = signedDocuments[item.id]?.url
        ? signedDocuments[item.id]
        : generated[item.id];
      if (!source?.url) {
        setActiveStep(2);
        return showMessage(
          `Verification PDF is missing for: ${item.title}`,
          "error",
        );
      }
      submittedDocuments[item.id] = {
        ...source,
        type: item.id,
        title: item.title,
        signedVersion: Number(
          source.signedVersion || source.version || 1,
        ),
        draftVersion: Number(
          source.draftVersion || source.version || 1,
        ),
        status: "SIGNED_AND_STAMPED_PDF_SUBMITTED",
        verificationStatus: "PENDING_PMC_VERIFICATION",
        source: signedDocuments[item.id]?.url
          ? "PACS_UPLOADED_SIGNED_COPY"
          : "SYSTEM_GENERATED_SIGNED_AND_STAMPED",
      };
    }

    const confirmation = window.confirm(
      "Submit all five system-generated signed-and-stamped PDFs to PMC? Documents will be locked until PMC raises a query.",
    );

    if (!confirmation) return;

    try {
      setBusy("submit");

      const submissionId = `${project.id}_PACS_DOCS_` + Date.now();

      const submittedAtIso = new Date().toISOString();

      const verificationSnapshot = Object.fromEntries(
        Object.entries(submittedDocuments).map(([type, record]) => [
          type,
          {
            ...record,
            submittedAtIso,
            submittedByUid: user.uid,
            submittedByEmail: user.email || "",
          },
        ]),
      );

      await updateDoc(doc(db, "godown_projects", project.id), {
        status: "DOCUMENTS_SUBMITTED_TO_PMC",

        documentStage: "PMC_UNDER_REVIEW",

        currentStage: "PACS Documents Under PMC Review",

        pmcDecision: "PENDING",

        documentSubmissionId: submissionId,

        documentSubmissionVersion:
          Number(project.documentSubmissionVersion || 0) + 1,

        // Compatibility snapshot consumed by every current PMC build. These
        // are the same system-generated signed-and-stamped PDFs, not a second
        // upload. Keeping the established field avoids a schema/rules break.
        signedPacsDocuments: verificationSnapshot,
        signedDocumentsUploadedCount: PACS_DOCUMENT_TYPES.length,

        documentQueryStatus: "NO_OPEN_QUERY",

        pmcSubmittedAt: serverTimestamp(),

        pmcSubmittedAtIso: submittedAtIso,

        pmcSubmittedByUid: user.uid,

        documentCenterStep: 4,
        documentCenterProgress: 100,
        progress: Math.max(Number(project.progress || 0), 25),

        updatedAt: serverTimestamp(),
      });

      await setDoc(
        doc(db, "notifications_pmc", `${project.id}_PACS_DOCUMENT_REVIEW`),
        {
          type: "NAFED_PACS_DOCUMENT_REVIEW",

          title: "PACS Document Verification Required",

          message: `${clean(
            base.organizationName,
          )} has submitted all five system-generated signed-and-stamped PACS PDFs for PMC verification.`,

          projectId: project.id,
          applicationId: clean(base.applicationId),

          submissionId,

          pacsId: user.uid,
          pacsName: clean(base.organizationName),

          state: clean(base.state),

          district: clean(base.district),

          documentCount: PACS_DOCUMENT_TYPES.length,

          documentSource: "signedPacsDocuments",

          evidenceAvailable: Boolean(savedEvidence?.photoUrl),

          status: "UNREAD",
          priority: "HIGH",

          createdAt: serverTimestamp(),

          createdAtIso: submittedAtIso,
        },
        { merge: true },
      );

      await setDoc(doc(db, "nafed_activity", submissionId), {
        action: "PACS_DOCUMENTS_SUBMITTED_TO_PMC",

        entityType: "GODOWN_PROJECT",

        entityId: project.id,

        applicationId: clean(base.applicationId),

        pacsUid: user.uid,

        performedByUid: user.uid,

        performedByEmail: user.email || "",

        performedByRole: "PACS",

        previousStage: project.documentStage || "PACS_DOCUMENT_PREPARATION",

        newStage: "PMC_UNDER_REVIEW",

        documentCount: PACS_DOCUMENT_TYPES.length,

        submissionId,

        createdAt: serverTimestamp(),

        createdAtIso: submittedAtIso,
      });

      onSaved?.({
        status: "DOCUMENTS_SUBMITTED_TO_PMC",

        documentStage: "PMC_UNDER_REVIEW",

        currentStage: "PACS Documents Under PMC Review",

        pmcDecision: "PENDING",

        documentSubmissionId: submissionId,
        signedPacsDocuments: verificationSnapshot,
        signedDocumentsUploadedCount: PACS_DOCUMENT_TYPES.length,
        documentCenterStep: 4,
        documentCenterProgress: 100,
        progress: Math.max(Number(project.progress || 0), 25),
      });

      showMessage(
        "All five system-generated signed-and-stamped PDFs have been submitted to PMC for verification.",
        "success",
      );
    } catch (error) {
      console.error("PMC submission failed:", error);

      showMessage(error.message || "PMC submission failed.", "error");
    } finally {
      setBusy("");
    }
  };

  const bilingualDocumentData = {
    ...base,
    ...form,
    pacsLetterhead: letterhead,
    letterheadUrl: letterhead.url || "",
  };

  const downloadPdfSet = async () => {
    try {
      const validationError = validateForm({ requireLetterhead: true });
      if (validationError) return showMessage(validationError, "error");
      setBusy("language-zip");
      const entries = [];
      for (let index = 0; index < PACS_DOCUMENT_TYPES.length; index += 1) {
        const item = PACS_DOCUMENT_TYPES[index];
        const html = buildPacsBilingualDocumentHtml(
          item.id,
          bilingualDocumentData,
          documentLanguage,
        );
        entries.push({
          name: `${String(index + 1).padStart(2, "0")}_${item.shortCode}_${documentLanguage}.pdf`,
          data: await officialHtmlToPdfBlob(
            html,
            `${item.shortCode}_${documentLanguage}.pdf`,
          ),
        });
      }
      entries.push({
        name: "manifest.json",
        data: JSON.stringify(
          {
            packageType: "PACS_RESOLUTIONS_AND_DECLARATIONS",
            language: documentLanguage,
            applicationId: base.applicationId,
            pacsName: base.organizationName,
            documentCount: PACS_DOCUMENT_TYPES.length,
            generatedAtIso: new Date().toISOString(),
          },
          null,
          2,
        ),
      });
      const zip = await createZipBlob(entries);
      downloadBlob(
        zip,
        `PACS_Resolutions_${safeFilePart(base.applicationId)}_${documentLanguage}.zip`,
      );
      showMessage(
        `All five ${documentLanguage.toLowerCase()} PDFs downloaded in one ZIP.`,
        "success",
      );
    } catch (error) {
      showMessage(error.message || "Five-document PDF ZIP could not be generated.", "error");
    } finally {
      setBusy("");
    }
  };

  const evidenceDeletePolicy = canDeletePacsDocumentRecord({
    project,
    record: savedEvidence || {},
    kind: "evidence",
    currentUid: auth.currentUser?.uid || "",
  });

  if (archived) {
    return (
      <div className="pdc">
        <style>{STYLE}</style>
        <div className="archived-project">
          <b>Application Removed from Active Records</b>
          <span>
            This archived application is not available for document generation,
            GPS evidence or PMC submission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="pdc">
      <style>{STYLE}</style>

      <header className="pdc-header">
        <div>
          <small>PACS OFFICIAL DOCUMENT WORKSPACE</small>
          <h1>Committee Documents & PMC Submission</h1>
          <p>
            Generate five official signed-and-stamped PDFs and submit the
            complete set directly for PMC verification.
          </p>
        </div>

        <div className="stage-box">
          <span>Current Stage</span>
          <b>{project.documentStage || "PACS_DOCUMENT_PREPARATION"}</b>
        </div>
      </header>

      {message.text && (
        <div className={`pdc-message ${message.type}`}>
          <span>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage({ type: "", text: "" })}
          >
            ×
          </button>
        </div>
      )}

      {reviewInProgress && !locked && (
        <div className="locked-note">
          <b>Document Set Under Review</b>
          <span>
            You may correct meeting/project details and regenerate the drafts.
            The saved Chairman signature, Secretary signature and society seal
            are applied automatically. All earlier generated versions remain preserved.
          </span>
        </div>
      )}

      <section className="summary-grid">
        <Summary
          label="Official PACS Letterhead"
          value={letterhead.url ? "Uploaded" : "Required"}
          status={
            letterhead.url
              ? `Auto-fit: ${letterhead.topSafeMm || 46}mm top / ${letterhead.bottomSafeMm || 24}mm bottom`
              : "Upload before PDF generation"
          }
        />

        <Summary
          label="Official Generated PDFs"
          value={`${generatedCount}/${PACS_DOCUMENT_TYPES.length}`}
          status={
            generatedCount === PACS_DOCUMENT_TYPES.length
              ? "Complete"
              : "Pending"
          }
        />

        <Summary
          label={
            REQUIRE_MEETING_EVIDENCE
              ? "Meeting Evidence (Required)"
              : "Submission Route"
          }
          value={
            REQUIRE_MEETING_EVIDENCE
              ? savedEvidence?.photoUrl
                ? "Available"
                : "Not Added"
              : "Direct to PMC"
          }
          status={
            REQUIRE_MEETING_EVIDENCE
              ? project.committeeVerificationStatus ||
                "Required Before PMC Submission"
              : "Photo and GPS evidence not required"
          }
        />

        <Summary
          label="PMC Verification"
          value={project.pmcDecision || "Pending"}
          status={project.documentStage || "Preparation"}
        />
      </section>

      <DocumentSteps
        activeStep={activeStep}
        onSelect={setActiveStep}
        generatedCount={generatedCount}
        evidenceReady={Boolean(savedEvidence?.photoUrl)}
        evidenceRequired={REQUIRE_MEETING_EVIDENCE}
        submitted={locked}
      />

      {activeStep === 1 && (
        <>
          <section className="pdc-card">
            <div className="section-heading">
              <i>1</i>
              <div>
                <h2>Managing Committee Meeting Details</h2>
                <p>
                  These details will appear in the official resolution and
                  related declarations.
                </p>
              </div>
            </div>

            <div className="form-grid">
              <Field
                label="Resolution Number"
                required
                value={form.resolutionNumber}
                disabled={locked}
                onChange={(value) => change("resolutionNumber", value)}
              />

              <Field
                label="Resolution Date"
                required
                type="date"
                value={form.resolutionDate}
                disabled={locked}
                onChange={(value) => change("resolutionDate", value)}
              />

              <Field
                label="Meeting Time"
                required
                type="time"
                value={form.meetingTime}
                disabled={locked}
                onChange={(value) => change("meetingTime", value)}
              />

              <Field
                label="Meeting Venue"
                required
                value={form.meetingPlace}
                disabled={locked}
                onChange={(value) => change("meetingPlace", value)}
              />

              <Field
                label="Agenda Item Number"
                required
                value={form.agendaNumber}
                disabled={locked}
                onChange={(value) => change("agendaNumber", value)}
              />

              <Field
                label="Total Committee Members"
                required
                type="number"
                min="1"
                value={form.totalCommitteeMembers}
                disabled={locked}
                onChange={(value) => change("totalCommitteeMembers", value)}
              />

              <Field
                label="Members Present"
                required
                type="number"
                min="1"
                value={form.membersPresent}
                disabled={locked}
                onChange={(value) => change("membersPresent", value)}
              />

              <label className="field">
                <span>
                  Quorum Status <b>*</b>
                </span>

                <select
                  value={form.quorumStatus}
                  disabled={locked}
                  onChange={(event) =>
                    change("quorumStatus", event.target.value)
                  }
                >
                  <option value="Quorum duly constituted">
                    Quorum duly constituted
                  </option>
                  <option value="Quorum not constituted">
                    Quorum not constituted
                  </option>
                </select>
              </label>

              <Field
                label="Chairman Name"
                required
                value={form.chairmanName}
                disabled={locked}
                onChange={(value) => change("chairmanName", value)}
              />

              <Field
                label="Secretary / CEO Name"
                required
                value={form.secretaryName}
                disabled={locked}
                onChange={(value) => change("secretaryName", value)}
              />
            </div>
          </section>

          <section className="pdc-card">
            <div className="section-heading">
              <i>2</i>
              <div>
                <h2>Project & Financial Details</h2>
                <p>
                  Verify these details before generating the complete document
                  set.
                </p>
              </div>
            </div>

            <div className="form-grid">
              <Field
                label="Godown Capacity (MT)"
                required
                type="number"
                min="1"
                value={form.capacityMt || base.capacityMt}
                disabled={locked}
                onChange={(value) => change("capacityMt", value)}
              />

              <label className="field">
                <span>Land Status *</span>

                <select
                  value={form.landOwnershipType}
                  disabled={locked}
                  onChange={(event) =>
                    change("landOwnershipType", event.target.value)
                  }
                >
                  <option value="OWNED">Owned by PACS</option>
                  <option value="LEASED">Registered Lease</option>
                  <option value="ALLOTTED">
                    Allotted by Competent Authority
                  </option>
                </select>
              </label>

              {form.landOwnershipType === "LEASED" && (
                <Field
                  label="Lease Period (Years)"
                  required
                  type="number"
                  min="1"
                  value={form.leaseYears}
                  disabled={locked}
                  onChange={(value) => change("leaseYears", value)}
                />
              )}

              <Field
                label="Contribution Rate Per MT"
                required
                type="number"
                min="1"
                value={form.contributionRate}
                disabled={locked}
                onChange={(value) => change("contributionRate", value)}
              />

              <Field
                label="PMC Preliminary Estimated Project Cost"
                type="number"
                min="0"
                value={
                  project.pmcTechnicalDocuments?.PMC_PRELIMINARY_ESTIMATE
                    ?.estimatedProjectCost ||
                  project.preliminaryEstimate?.estimatedProjectCost ||
                  project.estimatedProjectCost ||
                  ""
                }
                disabled
              />

              <Field
                label="PACS Own Contribution"
                type="number"
                min="0"
                value={form.ownContribution}
                disabled={locked}
                onChange={(value) => change("ownContribution", value)}
              />

              <Field
                label="Bank Finance Required"
                type="number"
                min="0"
                value={form.bankFinanceRequired}
                disabled={locked}
                onChange={(value) => change("bankFinanceRequired", value)}
              />

              <Field
                label="Latest Audit Year"
                placeholder="Example: 2025-26"
                value={form.latestAuditYear}
                disabled={locked}
                onChange={(value) => change("latestAuditYear", value)}
              />

              <Field
                label="Audit Classification"
                placeholder="Example: A Class"
                value={form.auditClassification}
                disabled={locked}
                onChange={(value) => change("auditClassification", value)}
              />

              <Field
                label="Applicable Cooperative Act"
                placeholder="Leave blank to use State/UT wording"
                value={form.cooperativeAct}
                disabled={locked}
                wide
                onChange={(value) => change("cooperativeAct", value)}
              />
            </div>

            <div className="details-save-bar">
              <div>
                <b>Confirm Meeting & Project Details</b>
                <span>
                  Save the entered time and all details before generating
                  documents.
                </span>
              </div>
              <button
                type="button"
                className="primary"
                onClick={saveProjectDetails}
                disabled={Boolean(busy) || locked}
              >
                {busy === "save-details"
                  ? "Saving Details..."
                  : "Save Details & Continue"}
              </button>
            </div>
          </section>
        </>
      )}

      {activeStep === 2 && (
        <section className="pdc-card">
          <div className="letterhead-panel">
            <div className="letterhead-copy">
              <small>MANDATORY PRINT TEMPLATE</small>
              <h2>Upload Official PACS Letterhead</h2>
              <p>
                Upload a clean full-A4 JPG, PNG or WEBP letterhead. The system
                detects its occupied header/footer and places every Gujarati or
                English draft only inside the safe writing area. The same
                letterhead is applied to both pages of the first resolution.
              </p>
              <label className="letterhead-file">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  disabled={Boolean(busy) || locked}
                  onChange={(event) =>
                    selectLetterhead(event.target.files?.[0] || null)
                  }
                />
                <span>
                  {letterheadFile?.name ||
                    letterhead.fileName ||
                    "Choose full-A4 letterhead image"}
                </span>
              </label>

              <div className="letterhead-margins">
                <Field
                  label="Top Safe Area (mm)"
                  type="number"
                  min="22"
                  max="88"
                  value={letterhead.topSafeMm}
                  disabled={Boolean(busy) || locked}
                  onChange={(value) =>
                    changeLetterheadMargin("topSafeMm", value)
                  }
                />
                <Field
                  label="Bottom Safe Area (mm)"
                  type="number"
                  min="12"
                  max="60"
                  value={letterhead.bottomSafeMm}
                  disabled={Boolean(busy) || locked}
                  onChange={(value) =>
                    changeLetterheadMargin("bottomSafeMm", value)
                  }
                />
                <Field
                  label="Left Margin (mm)"
                  type="number"
                  min="10"
                  max="35"
                  value={letterhead.leftSafeMm}
                  disabled={Boolean(busy) || locked}
                  onChange={(value) =>
                    changeLetterheadMargin("leftSafeMm", value)
                  }
                />
                <Field
                  label="Right Margin (mm)"
                  type="number"
                  min="10"
                  max="35"
                  value={letterhead.rightSafeMm}
                  disabled={Boolean(busy) || locked}
                  onChange={(value) =>
                    changeLetterheadMargin("rightSafeMm", value)
                  }
                />
              </div>

              <button
                type="button"
                className="primary"
                onClick={saveLetterhead}
                disabled={Boolean(busy) || locked}
              >
                {busy === "inspect-letterhead"
                  ? "Detecting Safe Area..."
                  : busy === "save-letterhead"
                    ? "Saving Letterhead..."
                    : letterhead.url
                      ? "Save Letterhead / Margin Adjustment"
                      : "Upload & Save Letterhead"}
              </button>
            </div>

            <div className={`letterhead-preview ${letterhead.url || letterhead.previewDataUrl ? "ready" : ""}`}>
              {letterhead.url || letterhead.previewDataUrl ? (
                <>
                  <img
                    src={letterhead.previewDataUrl || letterhead.url}
                    alt="PACS letterhead preview"
                  />
                  <div
                    className="letterhead-safe-overlay"
                    style={{
                      top: `${(Number(letterhead.topSafeMm || 46) / 297) * 100}%`,
                      bottom: `${(Number(letterhead.bottomSafeMm || 24) / 297) * 100}%`,
                      left: `${(Number(letterhead.leftSafeMm || 17) / 210) * 100}%`,
                      right: `${(Number(letterhead.rightSafeMm || 17) / 210) * 100}%`,
                    }}
                  >
                    <span>Document text auto-fit area</span>
                  </div>
                </>
              ) : (
                <span>Letterhead preview will appear here</span>
              )}
            </div>
          </div>

          <div className="section-heading action-heading">
            <div className="heading-group">
              <i>3</i>
              <div>
                <h2>Generate Five Official PDFs on PACS Letterhead</h2>
                <p>
                  Exact official wording is placed on the uploaded PACS
                  letterhead. New generation keeps every earlier version.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="primary"
              onClick={generateAndUploadAll}
              disabled={Boolean(busy) || locked}
            >
              {busy === "generate"
                ? "Generating & Uploading..."
                : `Generate & Save All (${documentLanguage === "GUJARATI" ? "Gujarati" : "English"})`}
            </button>
          </div>

          <div className="bilingual-panel language-panel">
            <div>
              <b>District Application/Resolution + Four Certificates</b>
              <span>
                Select one language. Every PDF is generated on the saved PACS
                letterhead and the already-uploaded Chairman signature,
                Secretary signature and society stamp are placed at the end.
              </span>
            </div>
            <label>
              <span>Document Language</span>
              <select
                value={documentLanguage}
                onChange={(event) => setDocumentLanguage(event.target.value)}
                disabled={Boolean(busy)}
              >
                <option value="GUJARATI">Gujarati</option>
                <option value="ENGLISH">English</option>
              </select>
            </label>
            <button
              type="button"
              className="primary"
              onClick={downloadPdfSet}
              disabled={Boolean(busy)}
            >
              {busy === "language-zip"
                ? "Building ZIP..."
                : `Download 5 ${documentLanguage === "GUJARATI" ? "Gujarati" : "English"} PDFs`}
            </button>
          </div>

          <div className="manual-signature-panel">
            <b>Saved Signature &amp; Seal Auto Mode</b>
            <span>
              Saved Chairman and Secretary signatures and the society stamp are
              added automatically to the last page. If any saved image is not
              available, a blank physical-signature space remains for manual
              signing and sealing after printing.
            </span>
          </div>

          <div className="document-list">
            {PACS_DOCUMENT_TYPES.map((item, index) => {
              const record = generated[item.id];
              const deletePolicy = canDeletePacsDocumentRecord({
                project,
                record: record || {},
                kind: "generated",
                currentUid: auth.currentUser?.uid || "",
              });

              return (
                <div className="document-row" key={item.id}>
                  <i>{index + 1}</i>

                  <div className="document-name">
                    <b>{item.title}</b>
                    <span>
                      {record?.url
                        ? `Version ${record.version || 1} · ${
                            record.documentNumber || "Document generated"
                          }`
                        : "Document not generated"}
                    </span>
                  </div>

                  <Status value={record?.status || "NOT GENERATED"} />

                  <button
                    type="button"
                    onClick={() => generateOne(item.id, "PREVIEW")}
                    disabled={Boolean(busy)}
                  >
                    Preview {documentLanguage === "GUJARATI" ? "Gujarati" : "English"}
                  </button>

                  <button
                    type="button"
                    onClick={() => generateOne(item.id, "DOWNLOAD")}
                    disabled={Boolean(busy)}
                  >
                    Download PDF
                  </button>

                  {record?.url && (
                    <a href={record.url} target="_blank" rel="noreferrer">
                      Saved PDF
                    </a>
                  )}

                  {record?.url && (
                    <button
                      type="button"
                      className="danger-button"
                      onClick={() => deletePacsDocument("generated", item.id)}
                      disabled={Boolean(busy) || !deletePolicy.allowed}
                      title={
                        deletePolicy.allowed
                          ? "Permanently delete this generated PDF"
                          : deletePolicy.reason
                      }
                    >
                      {busy === `delete-generated-${item.id}`
                        ? "Deleting..."
                        : "Delete PDF"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {false && (
        <section className="pdc-card">
          <div className="section-heading">
            <i>4</i>
            <div>
              <h2>Upload Authorised Signed Copies</h2>
              <p>
                Print the generated drafts, obtain authorised signatures and
                PACS seal, then upload each signed PDF.
              </p>
            </div>
          </div>

          <div className="signed-letterhead-strip">
            <div className="signed-letterhead-preview">
              {letterhead.previewDataUrl || letterhead.url ? (
                <img
                  src={letterhead.previewDataUrl || letterhead.url}
                  alt="Saved PACS letterhead"
                />
              ) : (
                <span>No letterhead</span>
              )}
            </div>
            <div className="signed-letterhead-copy">
              <b>Official PACS Letterhead</b>
              <span>
                {letterhead.url
                  ? `Saved version ${letterhead.version || 1}. Every generated page uses this letterhead.`
                  : "Letterhead must be saved before signed documents are accepted."}
              </span>
              <small>
                If you replace it here, the system returns to the draft step so
                all five PDFs can be regenerated on the new letterhead.
              </small>
            </div>
            <label className="letterhead-file compact">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                disabled={Boolean(busy) || locked}
                onChange={(event) =>
                  selectLetterhead(event.target.files?.[0] || null)
                }
              />
              <span>{letterheadFile?.name || "Choose / Replace Letterhead"}</span>
            </label>
            <button
              type="button"
              className="primary"
              onClick={saveLetterhead}
              disabled={Boolean(busy) || locked || (!letterheadFile && !letterhead.url)}
            >
              {busy === "inspect-letterhead"
                ? "Checking..."
                : busy === "save-letterhead"
                  ? "Saving..."
                  : letterheadFile
                    ? "Save New Letterhead"
                    : "Letterhead Saved"}
            </button>
          </div>

          <div className="signed-list">
            {PACS_DOCUMENT_TYPES.map((item, index) => {
              const saved = signedDocuments[item.id];
              const deletePolicy = canDeletePacsDocumentRecord({
                project,
                record: saved || {},
                kind: "signed",
                currentUid: auth.currentUser?.uid || "",
              });

              return (
                <div className="signed-row" key={item.id}>
                  <div className="signed-title">
                    <i>{index + 1}</i>
                    <span>
                      <b>{item.title}</b>
                      <small>
                        {saved?.url
                          ? `Uploaded ${formatDate(saved.uploadedAtIso)}`
                          : "Signed PDF pending"}
                      </small>
                    </span>
                  </div>

                  <label className="file-box">
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      disabled={locked}
                      onChange={(event) =>
                        selectSignedFile(
                          item.id,
                          event.target.files?.[0] || null,
                        )
                      }
                    />

                    <span>
                      {signedFiles[item.id]?.name || "Choose signed PDF"}
                    </span>
                  </label>

                  <Status
                    value={
                      saved?.verificationStatus || saved?.status || "PENDING"
                    }
                  />

                  {saved?.url && (
                    <a href={saved.url} target="_blank" rel="noreferrer">
                      View Signed PDF
                    </a>
                  )}

                  {saved?.url && (
                    <button
                      type="button"
                      className="danger-button"
                      onClick={() => deletePacsDocument("signed", item.id)}
                      disabled={Boolean(busy) || !deletePolicy.allowed}
                      title={
                        deletePolicy.allowed
                          ? "Permanently delete this signed PDF"
                          : deletePolicy.reason
                      }
                    >
                      {busy === `delete-signed-${item.id}`
                        ? "Deleting..."
                        : "Delete Signed"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="card-action">
            <button
              type="button"
              className="primary"
              onClick={uploadSignedDocuments}
              disabled={Boolean(busy) || locked}
            >
              {busy === "signed"
                ? "Uploading Signed Documents..."
                : "Upload All Signed Documents"}
            </button>
          </div>
        </section>
      )}

      {REQUIRE_MEETING_EVIDENCE && activeStep === 3 && (
        <section className="pdc-card">
          <div className="section-heading">
            <i>4</i>
            <div>
              <h2>Committee Meeting Photo &amp; GPS Evidence (Required)</h2>
              <p>
                Capture the committee meeting photo and live GPS location. PMC
                approval and MOU execution remain locked until this evidence is
                uploaded and verified.
              </p>
            </div>
          </div>

          <div className="evidence-grid">
            <label className="evidence-upload">
              <span>Committee Meeting Photo *</span>

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                disabled={locked}
                onChange={(event) => setPhoto(event.target.files?.[0] || null)}
              />

              <b>
                {photo?.name ||
                  (savedEvidence?.photoUrl
                    ? "Existing photo available"
                    : "Choose or capture photo")}
              </b>
            </label>

            <button
              type="button"
              className="gps-button"
              onClick={captureLocation}
              disabled={Boolean(busy) || locked}
            >
              {busy === "gps" ? "Capturing GPS..." : "Capture Current GPS"}
            </button>

            <div className={gps ? "gps-result captured" : "gps-result"}>
              {gps ? (
                <>
                  <b>✓ GPS Captured</b>
                  <span>
                    {Number(gps.latitude).toFixed(6)},{" "}
                    {Number(gps.longitude).toFixed(6)}
                  </span>
                  <small>
                    Accuracy: {Math.round(Number(gps.accuracy || 0))} metres
                  </small>
                </>
              ) : (
                <>
                  <b>GPS Not Captured</b>
                  <span>Current location is required</span>
                </>
              )}
            </div>

            <button
              type="button"
              className="success-button"
              onClick={uploadGpsEvidence}
              disabled={Boolean(busy) || locked}
            >
              {busy === "evidence"
                ? "Uploading Evidence..."
                : "Upload Meeting Evidence"}
            </button>

            {gpsPolicyBlocked && (
              <div className="gps-policy-help">
                <div>
                  <b>Browser has blocked GPS in this window</b>
                  <span>
                    Open the portal directly in a secure browser tab, allow
                    Location and capture again.
                  </span>
                </div>
                <button type="button" onClick={openGpsInDirectTab}>
                  Open Direct GPS Page
                </button>
              </div>
            )}
          </div>

          {savedEvidence?.photoUrl && (
            <div className="existing-evidence">
              <img
                src={savedEvidence.photoUrl}
                alt="Committee meeting evidence"
              />

              <div>
                <b>Committee Evidence Available</b>
                <span>
                  GPS: {savedEvidence.latitude}, {savedEvidence.longitude}
                </span>
                <span>
                  Status: {savedEvidence.status || "Pending PMC Verification"}
                </span>
              </div>

              <button
                type="button"
                className="danger-button evidence-delete"
                onClick={deleteCommitteeEvidence}
                disabled={Boolean(busy) || !evidenceDeletePolicy.allowed}
                title={
                  evidenceDeletePolicy.allowed
                    ? "Permanently delete committee photo and saved GPS evidence"
                    : evidenceDeletePolicy.reason
                }
              >
                {busy === "delete-evidence" ? "Deleting..." : "Delete Evidence"}
              </button>
            </div>
          )}
          <div className="card-action">
            <button
              type="button"
              onClick={() => setActiveStep(4)}
              disabled={Boolean(busy)}
            >
              Continue to PMC Submission
            </button>
          </div>
        </section>
      )}

      {activeStep === 4 && (
        <section className="final-submit">
          <div>
            <small>FINAL PACS ACTION</small>
            <h2>Submit Complete Document Set to PMC</h2>
            <p>
              Submission requires the saved PACS letterhead and all five PDFs
              generated on its current version. Saved signatures and society
              seal are already embedded. The complete document set can be
              submitted directly to PMC without photo or GPS evidence.
            </p>

            <div className="requirements">
              <span className={letterhead.url ? "done" : ""}>
                {letterhead.url ? "✓" : "○"} PACS Letterhead Saved
              </span>

              <span
                className={
                  draftsUseCurrentLetterhead ? "done" : ""
                }
              >
                {draftsUseCurrentLetterhead ? "✓" : "○"} Current-Letterhead
                Signed-and-Stamped PDFs Generated
              </span>

              {REQUIRE_MEETING_EVIDENCE && (
                <span className={savedEvidence?.photoUrl ? "done" : ""}>
                  {savedEvidence?.photoUrl ? "✓" : "○"} GPS Evidence Required
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={submitToPmc}
            disabled={
              Boolean(busy) ||
              locked ||
              !draftsUseCurrentLetterhead ||
              (REQUIRE_MEETING_EVIDENCE && !savedEvidence?.photoUrl)
            }
          >
            {busy === "submit"
              ? "Submitting to PMC..."
              : locked
                ? "Already Submitted"
                : "Submit to PMC Verification"}
          </button>
        </section>
      )}
    </div>
  );
}

function DocumentSteps({
  activeStep,
  onSelect,
  generatedCount,
  evidenceReady,
  evidenceRequired,
  submitted,
}) {
  const total = PACS_DOCUMENT_TYPES.length;
  const steps = [
    {
      step: 1,
      number: 1,
      title: "Project Details",
      note: "Committee and financial information",
      done: generatedCount > 0,
    },
    {
      step: 2,
      number: 2,
      title: "Generate Documents",
      note: `${generatedCount}/${total} signed-and-stamped PDFs generated`,
      done: generatedCount === total,
    },
    ...(evidenceRequired
      ? [
          {
            step: 3,
            number: 3,
            title: "Photo & GPS Evidence",
            note: evidenceReady
              ? "Meeting evidence uploaded"
              : "Required before submission",
            done: evidenceReady,
          },
        ]
      : []),
    {
      step: 4,
      number: evidenceRequired ? 4 : 3,
      title: "Submit to PMC",
      note: submitted ? "Submitted and locked" : "Final submission pending",
      done: submitted,
    },
  ];

  return (
    <nav
      className={`document-steps ${evidenceRequired ? "four-steps" : ""}`}
      aria-label="PACS document preparation steps"
    >
      {steps.map((step) => (
        <button
          type="button"
          key={step.step}
          className={`${activeStep === step.step ? "active" : ""} ${step.done ? "done" : ""}`}
          onClick={() => onSelect(step.step)}
        >
          <i>{step.done ? "✓" : step.number}</i>
          <span>
            <b>{step.title}</b>
            <small>{step.note}</small>
          </span>
        </button>
      ))}
    </nav>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  disabled = false,
  wide = false,
  ...props
}) {
  return (
    <label className={`field ${wide ? "wide" : ""}`}>
      <span>
        {label}
        {required && <b> *</b>}
      </span>

      <input
        type={type}
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        {...props}
      />
    </label>
  );
}

function Summary({ label, value, status }) {
  return (
    <div className="summary-card">
      <span>{label}</span>
      <b>{value}</b>
      <small>{status}</small>
    </div>
  );
}

function Status({ value }) {
  const text = String(value || "PENDING").replaceAll("_", " ");

  const approved = /approved|complete|uploaded|generated/i.test(text);

  const rejected = /rejected|query|failed/i.test(text);

  return (
    <span
      className={`status ${
        approved ? "approved" : rejected ? "rejected" : "pending"
      }`}
    >
      {text}
    </span>
  );
}

const STYLE = `
.pdc{
  color:#102749;
  font-family:Inter,"Segoe UI",Arial,sans-serif;
}
.pdc *{box-sizing:border-box}
.pdc-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:20px;
  padding:17px 19px;
  margin-bottom:14px;
  color:#173754;
  border:1px solid #cbd8e5;
  border-left:5px solid #087646;
  border-radius:12px;
  background:#fff;
  box-shadow:0 7px 20px rgba(23,57,105,.06);
}
.pdc-header small{
  color:#087646;
  font-size:10px;
  font-weight:900;
  letter-spacing:.11em;
}
.pdc-header h1{
  margin:6px 0 4px;
  font-size:21px;
}
.pdc-header p{
  max-width:680px;
  margin:0;
  color:#6b7b8d;
  font-size:10px;
}
.stage-box{
  min-width:205px;
  padding:12px;
  border:1px solid #ccd8e5;
  border-radius:11px;
  background:#f6f8fb;
}
.stage-box span,.stage-box b{
  display:block;
}
.stage-box span{
  margin-bottom:5px;
  color:#718096;
  font-size:9px;
  text-transform:uppercase;
}
.stage-box b{
  font-size:11px;
  overflow-wrap:anywhere;
}
.pdc-message{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:15px;
  padding:12px 15px;
  margin-bottom:13px;
  border:1px solid #b8daf7;
  border-radius:10px;
  background:#edf7ff;
  color:#14538d;
  font-size:12px;
  font-weight:800;
}
.pdc-message.success{
  border-color:#addbbd;
  background:#ebf9f0;
  color:#08753a;
}
.pdc-message.error{
  border-color:#f0b5b5;
  background:#fff0f0;
  color:#b3261e;
}
.pdc-message button{
  border:0;
  background:transparent;
  color:inherit;
  font-size:18px;
  cursor:pointer;
}
.locked-note{
  display:flex;
  align-items:center;
  gap:15px;
  padding:13px 15px;
  margin-bottom:14px;
  border:1px solid #e6c776;
  border-radius:10px;
  background:#fff7df;
  color:#825400;
}
.locked-note b,.locked-note span{
  display:block;
}
.locked-note span{
  font-size:11px;
}
.summary-grid{
  display:grid;
  grid-template-columns:repeat(4,minmax(0,1fr));
  gap:12px;
  margin-bottom:14px;
}
.summary-card{
  padding:11px 13px;
  border:1px solid #d6e0eb;
  border-radius:12px;
  background:#fff;
  box-shadow:0 6px 18px rgba(23,57,105,.05);
}
.summary-card span,.summary-card b,.summary-card small{
  display:block;
}
.summary-card span{
  color:#65738a;
  font-size:10px;
  font-weight:800;
  text-transform:uppercase;
}
.summary-card b{
  margin:4px 0;
  color:#073b78;
  font-size:18px;
}
.document-steps{
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:8px;
  margin-bottom:14px;
  padding:11px;
  border:1px solid #d4dee9;
  border-radius:12px;
  background:#fff;
}
.document-steps.four-steps{
  grid-template-columns:repeat(4,minmax(0,1fr));
}
.document-steps>button{
  display:flex;
  align-items:center;
  gap:8px;
  min-width:0;
  padding:10px 8px;
  border:1px solid #d9e2ec;
  background:#f8fafc;
  color:#52667d;
  text-align:left;
  white-space:normal;
}
.document-steps>button>i{
  display:grid;
  place-items:center;
  flex:0 0 27px;
  width:27px;
  height:27px;
  border-radius:50%;
  background:#e3e9ef;
  color:#68798b;
  font-size:9px;
  font-style:normal;
}
.document-steps span,.document-steps b,.document-steps small{display:block;min-width:0}
.document-steps b{font-size:9px;color:#263f58}
.document-steps small{margin-top:3px;font-size:7px;color:#788698;line-height:1.35}
.document-steps>button.active{
  border-color:#0758bb;
  background:#eef6ff;
  box-shadow:0 0 0 2px rgba(7,88,187,.09);
}
.document-steps>button.done i{background:#118249;color:#fff}
.archived-project{
  padding:30px;
  border:1px solid #e1b4b4;
  border-radius:12px;
  background:#fff4f4;
  color:#8e2925;
  text-align:center;
}
.archived-project b,.archived-project span{display:block}
.archived-project span{margin-top:7px;font-size:11px;color:#7c5555}
.summary-card small{
  color:#718096;
  font-size:9px;
  overflow-wrap:anywhere;
}
.pdc-card{
  padding:19px;
  margin-bottom:14px;
  border:1px solid #d6e0eb;
  border-radius:14px;
  background:#fff;
  box-shadow:0 8px 24px rgba(23,57,105,.055);
}
.section-heading{
  display:flex;
  align-items:center;
  gap:11px;
  margin-bottom:16px;
}
.section-heading>i,.heading-group>i{
  display:grid;
  place-items:center;
  flex:0 0 34px;
  width:34px;
  height:34px;
  border-radius:50%;
  background:#e8f3ff;
  color:#0758bb;
  font-style:normal;
  font-weight:900;
}
.section-heading h2{
  margin:0;
  font-size:17px;
}
.section-heading p{
  margin:4px 0 0;
  color:#6b778b;
  font-size:11px;
}
.action-heading{
  justify-content:space-between;
}
.heading-group{
  display:flex;
  align-items:center;
  gap:11px;
}
.form-grid{
  display:grid;
  grid-template-columns:repeat(4,minmax(0,1fr));
  gap:12px;
}
.field span{
  display:block;
  margin-bottom:5px;
  color:#53637a;
  font-size:9px;
  font-weight:900;
  text-transform:uppercase;
}
.field span b{color:#d32f2f}
.field input,.field select{
  width:100%;
  height:42px;
  padding:0 11px;
  border:1px solid #ccd8e7;
  border-radius:8px;
  outline:none;
  background:#fff;
  color:#102749;
}
.field input:focus,.field select:focus{
  border-color:#1674cc;
  box-shadow:0 0 0 3px rgba(22,116,204,.1);
}
.field input:disabled,.field select:disabled{
  background:#f0f3f7;
  color:#738096;
}
.field.wide{grid-column:span 2}
.pdc button,.pdc a{
  border:1px solid #9dbce0;
  border-radius:7px;
  background:#fff;
  color:#0758bb;
  padding:8px 10px;
  font-size:10px;
  font-weight:850;
  text-decoration:none;
  cursor:pointer;
  white-space:nowrap;
}
.pdc button.primary{
  border-color:#0758bb;
  background:#0758bb;
  color:#fff;
  padding:10px 15px;
}
.pdc button.danger-button{
  border-color:#e2a3a0;
  background:#fff5f5;
  color:#b42318;
}
.pdc button.danger-button:hover:not(:disabled){
  border-color:#b42318;
  background:#b42318;
  color:#fff;
}
.pdc button:disabled{
  opacity:.52;
  cursor:not-allowed;
}
.document-list,.signed-list{
  overflow:hidden;
  border:1px solid #d9e2ed;
  border-radius:10px;
}
.document-row{
  display:grid;
  grid-template-columns:32px minmax(240px,1fr) 145px auto auto auto auto;
  align-items:center;
  gap:9px;
  padding:11px;
  border-bottom:1px solid #e4eaf1;
}
.document-row:last-child,.signed-row:last-child{
  border-bottom:0;
}
.document-row>i{
  display:grid;
  place-items:center;
  width:28px;
  height:28px;
  border-radius:50%;
  background:#eaf3ff;
  color:#0758bb;
  font-style:normal;
  font-weight:900;
}
.document-name b,.document-name span{
  display:block;
}
.document-name b{
  font-size:11px;
}
.document-name span{
  margin-top:4px;
  color:#778398;
  font-size:9px;
}
.status{
  width:max-content;
  max-width:150px;
  padding:6px 8px;
  border-radius:20px;
  background:#fff3dc;
  color:#936000;
  font-size:8px;
  font-weight:900;
  text-align:center;
  text-transform:uppercase;
}
.status.approved{
  background:#e7f7ed;
  color:#087b38;
}
.status.rejected{
  background:#ffebeb;
  color:#b3261e;
}
.signed-row{
  display:grid;
  grid-template-columns:minmax(270px,1.2fr) minmax(200px,.8fr) 145px auto auto;
  align-items:center;
  gap:10px;
  padding:11px;
  border-bottom:1px solid #e4eaf1;
}
.signed-title{
  display:flex;
  align-items:center;
  gap:9px;
}
.signed-title>i{
  display:grid;
  place-items:center;
  flex:0 0 28px;
  width:28px;
  height:28px;
  border-radius:50%;
  background:#eff6fd;
  color:#0758bb;
  font-style:normal;
  font-weight:900;
}
.signed-title b,.signed-title small{
  display:block;
}
.signed-title b{font-size:10px}
.signed-title small{
  margin-top:3px;
  color:#788598;
  font-size:8px;
}
.file-box{
  position:relative;
  overflow:hidden;
}
.file-box input{
  position:absolute;
  width:1px;
  height:1px;
  opacity:0;
}
.file-box span{
  display:block;
  overflow:hidden;
  padding:9px;
  border:1px dashed #9db3cc;
  border-radius:7px;
  color:#52677f;
  font-size:9px;
  text-overflow:ellipsis;
  white-space:nowrap;
  cursor:pointer;
}
.card-action{
  display:flex;
  justify-content:flex-end;
  margin-top:13px;
}
.evidence-grid{
  display:grid;
  grid-template-columns:1.2fr .7fr 1.1fr .8fr;
  align-items:end;
  gap:12px;
}
.evidence-upload>span{
  display:block;
  margin-bottom:5px;
  color:#53637a;
  font-size:9px;
  font-weight:900;
  text-transform:uppercase;
}
.evidence-upload input{
  width:100%;
  padding:8px;
  border:1px solid #ccd8e7;
  border-radius:8px;
}
.evidence-upload b{
  display:block;
  margin-top:5px;
  overflow:hidden;
  color:#6f7d91;
  font-size:8px;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.gps-button,.success-button{
  min-height:42px;
}
.pdc .success-button{
  border-color:#0b9343;
  background:#0b9343;
  color:#fff;
}
.gps-result{
  min-height:58px;
  padding:9px;
  border:1px solid #e3ca8a;
  border-radius:8px;
  background:#fff7e5;
  color:#886000;
  font-size:9px;
}
.gps-result.captured{
  border-color:#b9dcc6;
  background:#edf8f1;
  color:#08763a;
}
.gps-result b,.gps-result span,.gps-result small{
  display:block;
}
.gps-result span{margin-top:4px}
.gps-result small{margin-top:3px}
.gps-policy-help{
  grid-column:1/-1;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:11px 12px;
  border:1px solid #e3ca8a;
  border-radius:8px;
  background:#fff7e5;
  color:#704f00;
}
.gps-policy-help b,.gps-policy-help span{display:block}
.gps-policy-help b{font-size:10px}
.gps-policy-help span{margin-top:3px;font-size:8.5px}
.gps-policy-help button{
  flex:0 0 auto;
  min-height:38px;
  border-color:#0a4b91;
  background:#0a4b91;
  color:#fff;
}
.existing-evidence{
  display:flex;
  align-items:center;
  gap:12px;
  margin-top:13px;
  padding:11px;
  border:1px solid #c6e2d0;
  border-radius:9px;
  background:#f1faf4;
}
.existing-evidence img{
  width:95px;
  height:70px;
  border-radius:7px;
  object-fit:cover;
}
.existing-evidence b,.existing-evidence span{
  display:block;
}
.existing-evidence>div{
  flex:1;
}
.existing-evidence .evidence-delete{
  flex:0 0 auto;
  margin-left:auto;
}
.existing-evidence b{
  color:#08753a;
  font-size:11px;
}
.existing-evidence span{
  margin-top:5px;
  color:#567062;
  font-size:9px;
}
.final-submit{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:25px;
  padding:21px;
  border-radius:15px;
  background:linear-gradient(135deg,#062c63,#075aac);
  color:#fff;
}
.final-submit small{
  color:#9fe8be;
  font-weight:900;
  letter-spacing:.09em;
}
.final-submit h2{
  margin:5px 0;
  font-size:20px;
}
.final-submit p{
  margin:0;
  color:#d8e8fb;
  font-size:10px;
}
.final-submit>button{
  border-color:#19a653;
  background:#19a653;
  color:#fff;
  padding:12px 17px;
}
.letterhead-panel{
  display:grid;
  grid-template-columns:minmax(0,1.25fr) minmax(280px,.75fr);
  gap:22px;
  margin:0 0 22px;
  padding:20px;
  border:1px solid #b9c9da;
  border-left:5px solid #087646;
  border-radius:13px;
  background:#f8fbfd;
}
.letterhead-copy small{
  color:#087646;
  font-size:9px;
  font-weight:900;
  letter-spacing:.1em;
}
.letterhead-copy h2{margin:5px 0 7px;font-size:18px}
.letterhead-copy p{margin:0 0 13px;color:#5d6f82;font-size:11px;line-height:1.55}
.letterhead-file{display:block;position:relative;margin-bottom:14px;overflow:hidden}
.letterhead-file input{position:absolute;width:1px;height:1px;opacity:0}
.letterhead-file span{
  display:block;padding:12px 14px;border:1.5px dashed #7899b8;border-radius:9px;
  background:white;color:#264b6d;font-size:11px;font-weight:800;cursor:pointer;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
}
.letterhead-margins{
  display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:14px;
}
.letterhead-preview{
  display:grid;place-items:center;position:relative;min-height:330px;overflow:hidden;
  border:1px dashed #9bb0c4;border-radius:10px;background:#eef3f7;color:#708296;
  font-size:11px;text-align:center;
}
.letterhead-preview.ready{background:#dfe7ee}
.letterhead-preview img{display:block;width:100%;height:100%;max-height:430px;object-fit:contain;background:white}
.letterhead-safe-overlay{
  position:absolute;border:2px dashed #0b7f48;background:rgba(11,127,72,.08);
  box-shadow:0 0 0 999px rgba(0,0,0,.06);pointer-events:none;
}
.letterhead-safe-overlay span{
  position:absolute;top:5px;left:50%;transform:translateX(-50%);padding:3px 7px;
  border-radius:20px;background:#087646;color:white;font-size:8px;font-weight:900;white-space:nowrap;
}
.manual-signature-panel{
  display:flex;
  align-items:flex-start;
  gap:14px;
  margin:16px 0;
  padding:16px 18px;
  border:1px solid #9fc3e6;
  border-left:4px solid #0b5cab;
  border-radius:12px;
  background:#f3f8fe;
}
.manual-signature-panel b{
  flex:0 0 auto;
  color:#0f3158;
  font-size:13px;
}
.manual-signature-panel span{
  color:#526b84;
  font-size:11px;
  line-height:1.55;
}
.signed-letterhead-strip{
  display:grid;
  grid-template-columns:68px minmax(240px,1fr) minmax(210px,300px) auto;
  align-items:center;
  gap:14px;
  margin:0 0 18px;
  padding:14px;
  border:1px solid #b7cadd;
  border-left:4px solid #087646;
  border-radius:11px;
  background:#f7fbf9;
}
.signed-letterhead-preview{
  display:grid;
  place-items:center;
  width:62px;
  height:82px;
  overflow:hidden;
  border:1px solid #cbd6df;
  border-radius:5px;
  background:#fff;
  color:#718096;
  font-size:8px;
  text-align:center;
}
.signed-letterhead-preview img{display:block;width:100%;height:100%;object-fit:contain}
.signed-letterhead-copy{display:grid;gap:4px}
.signed-letterhead-copy b{color:#123e30;font-size:13px}
.signed-letterhead-copy span{color:#526b60;font-size:10px}
.signed-letterhead-copy small{color:#75857e;font-size:9px;line-height:1.45}
.letterhead-file.compact{margin:0}
.letterhead-file.compact span{padding:10px 12px;background:#fff;font-size:10px}

.details-save-bar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:20px;
  margin-top:22px;
  padding:16px 18px;
  border:1px solid #cbd8e6;
  border-radius:11px;
  background:#f8fafc;
}
.details-save-bar>div{
  display:flex;
  flex-direction:column;
  gap:4px;
}
.details-save-bar b{
  color:#102749;
  font-size:14px;
}
.details-save-bar span{
  color:#64748b;
  font-size:11px;
}

/* Professional readable workspace */
.pdc{
  width:100%;
  max-width:1500px;
  margin:0 auto;
  padding:4px 8px 28px;
  font-size:14px;
  line-height:1.45;
}
.pdc-header{
  padding:22px 24px;
  margin-bottom:18px;
  border-radius:14px;
}
.pdc-header h1{font-size:25px}
.pdc-header p{font-size:12px}
.pdc-header small{font-size:11px}
.stage-box{padding:15px 16px}
.stage-box span{font-size:10px}
.stage-box b{font-size:13px}
.summary-grid,.document-steps{margin-bottom:18px}
.summary-card{padding:16px 18px}
.summary-card span{font-size:11px}
.summary-card b{font-size:22px}
.summary-card small{font-size:10px}
.document-steps{padding:13px;gap:10px}
.document-steps>button{min-height:62px;padding:12px 11px}
.document-steps b{font-size:11px}
.document-steps small{font-size:9px}
.pdc-card{
  padding:24px;
  margin-bottom:18px;
  border-radius:15px;
}
.section-heading{margin-bottom:22px}
.section-heading h2{font-size:20px}
.section-heading p{font-size:12px}
.section-heading>i,.heading-group>i{
  flex-basis:38px;
  width:38px;
  height:38px;
}
.form-grid{
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:18px 16px;
}
.field span{
  margin-bottom:7px;
  font-size:11px;
  letter-spacing:.025em;
}
.field input,.field select{
  height:48px;
  padding:0 14px;
  border-radius:9px;
  font-size:14px;
}
.field input[type="time"],.field input[type="date"]{
  color-scheme:light;
  cursor:text;
}
.pdc button,.pdc a{
  padding:10px 13px;
  font-size:11px;
}
.pdc button.primary{padding:12px 18px}
.document-row,.signed-row{padding:14px}
.document-name b,.signed-title b{font-size:12px}
.document-name span,.signed-title small{font-size:10px}
.status{font-size:9px}
.locked-note{padding:15px 18px;margin-bottom:18px}
.locked-note b{font-size:13px}
.locked-note span{font-size:11px}
.final-submit{
  background:#0b315f;
  border:1px solid #08284e;
}
.requirements{
  display:flex;
  flex-wrap:wrap;
  gap:7px;
  margin-top:12px;
}
.requirements span{
  padding:6px 8px;
  border-radius:20px;
  background:rgba(255,255,255,.12);
  color:#d4e4f8;
  font-size:8px;
  font-weight:800;
}
.requirements span.done{
  background:rgba(31,190,97,.25);
  color:#aaf4c7;
}
.bilingual-panel{
  display:grid;
  grid-template-columns:minmax(280px,1fr) auto auto;
  align-items:center;
  gap:10px;
  margin:0 0 18px;
  padding:16px;
  border:1px solid #bfd8ed;
  border-radius:13px;
  background:linear-gradient(135deg,#eff8ff,#f4fff8);
}
.bilingual-panel>div{display:grid;gap:4px}
.bilingual-panel b{color:#0a4778;font-size:14px}
.bilingual-panel span{color:#61758a;font-size:11px;line-height:1.5}
.bilingual-panel label{display:grid;gap:5px;font-size:10px;font-weight:800;color:#355675}
.bilingual-panel select{min-width:135px;border:1px solid #9cb7cd;border-radius:8px;padding:9px 11px;background:white;color:#153d62;font-weight:800}
.bilingual-panel button{
  border:1px solid #12689f;
  border-radius:9px;
  padding:10px 12px;
  background:white;
  color:#0d5687;
  font-weight:800;
}
.bilingual-panel button.primary{background:#0878b9;color:white}
@media(max-width:1100px){
  .summary-grid,.form-grid{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }
  .document-row{
    grid-template-columns:32px minmax(240px,1fr) 140px;
  }
  .document-row button,.document-row a{
    width:100%;
  }
  .bilingual-panel{grid-template-columns:1fr 1fr}
  .bilingual-panel>div{grid-column:1/-1}
  .letterhead-panel{grid-template-columns:1fr}
  .letterhead-margins{grid-template-columns:repeat(2,minmax(0,1fr))}
  .signed-letterhead-strip{grid-template-columns:68px minmax(220px,1fr) minmax(190px,260px)}
  .signed-letterhead-strip>button{grid-column:2/-1}
  .signed-row{
    grid-template-columns:1fr 1fr;
  }
  .evidence-grid{
    grid-template-columns:repeat(2,1fr);
  }
  .document-steps,.document-steps.four-steps{grid-template-columns:repeat(3,1fr)}
}
@media(max-width:1280px){
  .form-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
}
@media(max-width:650px){
  .pdc-header,.action-heading,.final-submit{
    display:block;
  }
  .stage-box{
    margin-top:13px;
  }
  .summary-grid,.form-grid,.evidence-grid{
    grid-template-columns:1fr;
  }
  .document-steps,.document-steps.four-steps{grid-template-columns:1fr}
  .field.wide{grid-column:auto}
  .action-heading>button{
    width:100%;
    margin-top:12px;
  }
  .document-row,.signed-row{
    display:flex;
    align-items:stretch;
    flex-direction:column;
  }
  .existing-evidence{
    align-items:stretch;
    flex-direction:column;
  }
  .existing-evidence .evidence-delete{
    width:100%;
    margin-left:0;
  }
  .manual-signature-panel{
    flex-direction:column;
  }
  .letterhead-margins{grid-template-columns:1fr}
  .signed-letterhead-strip{display:flex;align-items:stretch;flex-direction:column}
  .signed-letterhead-preview{align-self:center;width:92px;height:124px}
  .signed-letterhead-strip>button{width:100%}
  .details-save-bar{
    align-items:stretch;
    flex-direction:column;
  }
  .details-save-bar button{width:100%}
  .document-row>i{
    display:none;
  }
  .status{max-width:100%}
  .final-submit>button{
    width:100%;
    margin-top:16px;
  }
}
`;
